// Kiks Product Components - Web Components Architecture

(function () {
    'use strict';

    // ============================================================================
    // Shared Utilities
    // ============================================================================

    const productDataCache = new Map();
    const productDataResolved = new Map();
    const formRefreshPromises = new WeakMap();
    const formRefreshRequestIds = new WeakMap();
    const syncingForms = new WeakSet();
    const deferredRefreshTimers = new WeakMap();

    function escapeSelector(value) {
        if (value == null) return '';
        if (window.CSS && typeof window.CSS.escape === 'function') {
            return window.CSS.escape(String(value));
        }
        return String(value).replace(/"/g, '\\"');
    }

    function formatMoney(cents) {
        let amount = Number(cents);
        if (!Number.isFinite(amount)) return '';
        if (!Number.isInteger(amount)) {
            amount = Math.round(amount * 100);
        }

        const formatter =
            (window.Shopify && typeof window.Shopify.formatMoney === 'function'
                ? window.Shopify.formatMoney
                : null);

        if (formatter) {
            const format = (window.theme && window.theme.moneyFormat) || window.Shopify.money_format || '{{amount}}';
            return formatter(amount, format);
        }

        const normalized = (amount / 100).toFixed(2);
        return `$${normalized}`;
    }

    function formatMoneyWithoutCents(cents) {
        let amount = Number(cents);
        if (!Number.isFinite(amount)) return '';
        if (!Number.isInteger(amount)) {
            amount = Math.round(amount * 100);
        }

        const formatter =
            (window.Shopify && typeof window.Shopify.formatMoney === 'function'
                ? window.Shopify.formatMoney
                : null);

        if (formatter) {
            const format = (window.theme && window.theme.moneyFormat) || window.Shopify.money_format || '{{amount}}';
            let formatted = formatter(amount, format);
            // Remove .00 for whole numbers (handles various formats like $250.00, 250.00, etc.)
            formatted = formatted.replace(/\.00(?=\s|$)/g, '');
            return formatted;
        }

        const normalized = amount / 100;
        // Remove .00 for whole numbers
        if (normalized % 1 === 0) {
            return `$${normalized}`;
        }
        return `$${normalized.toFixed(2)}`;
    }

    function normalizeMoneyValue(value) {
        if (value == null) return null;
        if (typeof value === 'string' && value.trim() === '') return null;
        let amount = Number(value);
        if (!Number.isFinite(amount)) return null;
        if (!Number.isInteger(amount)) {
            amount = Math.round(amount * 100);
        }
        return amount;
    }

    function getPriceWrapperFromPicker(picker) {
        if (!picker) return null;
        const productId = picker.dataset.productId;
        const sectionId = picker.dataset.section;
        if (!productId || !sectionId) return null;
        return document.getElementById(`product-price-${productId}-${sectionId}`);
    }

    function updatePriceWrapper(wrapper, variant) {
        if (!wrapper || !variant) return;
        const priceValue = normalizeMoneyValue(variant.price);
        if (priceValue == null) return;

        const compareAtValue = normalizeMoneyValue(variant.compare_at_price);
        const hasCompare = compareAtValue != null && compareAtValue > priceValue;

        const currentPriceEl = wrapper.querySelector('[data-kiks-current-price]') ||
            wrapper.querySelector('.product-price__current-price');
        if (currentPriceEl) {
            currentPriceEl.textContent = formatMoney(priceValue);
        }

        const comparePriceSpan = wrapper.querySelector('[data-kiks-compare-price]');
        const oldPriceContainer = comparePriceSpan ?
            comparePriceSpan.closest('.product-price__old-price') :
            wrapper.querySelector('.product-price__old-price');

        if (oldPriceContainer) {
            if (hasCompare) {
                const compareTarget = comparePriceSpan || oldPriceContainer;
                compareTarget.textContent = formatMoney(compareAtValue);
                oldPriceContainer.hidden = false;
                oldPriceContainer.style.removeProperty('display');
            } else {
                oldPriceContainer.hidden = true;
            }
        }

        const badgeEl = wrapper.querySelector('.kik-save-amount-badge');
        if (badgeEl) {
            if (hasCompare) {
                const saveAmount = formatMoney(compareAtValue - priceValue);
                const template = badgeEl.dataset.kiksSaveBadgeTemplate || badgeEl.textContent || '';
                let badgeText = '';
                if (template.includes('[amount]')) {
                    badgeText = template.replace('[amount]', saveAmount);
                } else {
                    badgeText = `SAVE ${saveAmount}`;
                }
                badgeEl.textContent = badgeText;
                badgeEl.hidden = false;
                badgeEl.style.removeProperty('display');
            } else {
                badgeEl.hidden = true;
            }
        }
    }

    function shouldSyncVariantUrl(picker) {
        if (!picker) return false;
        const pickerUrl = picker.dataset && picker.dataset.url;
        if (!pickerUrl) return false;
        try {
            const pickerPath = new URL(pickerUrl, window.location.origin).pathname;
            return pickerPath === window.location.pathname;
        } catch (error) {
            return false;
        }
    }

    function syncVariantIdToUrl(variantId, picker) {
        if (!variantId || !shouldSyncVariantUrl(picker)) return;
        let url;
        try {
            url = new URL(window.location.href);
        } catch (error) {
            return;
        }
        url.searchParams.set('variant', String(variantId));
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }

    function getPickerVariants(picker) {
        if (!picker) return null;
        if (picker._kiksVariants) return picker._kiksVariants;
        const script = picker.querySelector('[data-variant-picker-variants]');
        if (!script) return null;
        try {
            const parsed = JSON.parse(script.textContent || '[]');
            if (Array.isArray(parsed)) {
                picker._kiksVariants = parsed.map((variant) => normalizeVariantOptions(variant));
                return picker._kiksVariants;
            }
        } catch (error) {
            console.warn('[kiks] Failed to parse picker variants JSON', error);
        }
        return null;
    }

    function getVariantForPicker(picker, input = null) {
        if (!picker) return null;
        const variants = getPickerVariants(picker);
        if (!variants || variants.length === 0) return null;

        const selections = getSelectedOptionValuesForPicker(picker);
        let optionCount = 0;
        const handle = picker.getAttribute('handle') || picker.dataset.handle;
        const product = getCachedProduct(handle);
        if (product?.options?.length) {
            optionCount = product.options.length;
        }
        if (optionCount === 0 && Array.isArray(variants[0]?.options)) {
            optionCount = variants[0].options.length;
        }
        while (selections.length < optionCount) {
            selections.push(null);
        }

        let variant = findVariantMatchingOptions({ variants }, selections);
        if (!variant && input) {
            const optionIndex = getOptionIndexFromInputName(input.name);
            const optionValue = input.value;
            if (optionIndex != null && optionValue) {
                variant = variants.find((candidate) =>
                    Array.isArray(candidate.options) && candidate.options[optionIndex] === optionValue
                );
            }
        }
        if (!variant) {
            variant = variants.find((candidate) => candidate.available) || variants[0];
        }
        return variant || null;
    }

    function getVariantIdForPicker(picker) {
        if (!picker) return null;
        const variant = getVariantForPicker(picker);
        if (variant) {
            const variantId = Number(variant.id);
            return Number.isFinite(variantId) ? variantId : null;
        }
        const handle = picker.getAttribute('handle') || picker.dataset.handle;
        const product = getCachedProduct(handle);
        if (!product) {
            const cachedId = Number(picker.dataset.kiksSelectedVariantId);
            return Number.isFinite(cachedId) ? cachedId : null;
        }
        const selections = getSelectedOptionValuesForPicker(picker);
        const optionCount = product.options?.length || 0;
        while (selections.length < optionCount) {
            selections.push(null);
        }
        let fallbackVariant = findVariantBySelections(product, selections);
        if (!fallbackVariant) {
            fallbackVariant = product.variants?.find((candidate) => candidate?.available) || product.variants?.[0];
        }
        if (!fallbackVariant) return null;
        normalizeVariantOptions(fallbackVariant);
        const fallbackId = Number(fallbackVariant.id);
        return Number.isFinite(fallbackId) ? fallbackId : null;
    }

    function buildFetchUrlWithVariant(picker, clickedInputEl, variantId) {
        if (!picker) return null;
        const baseUrl = (clickedInputEl && clickedInputEl.dataset && clickedInputEl.dataset.productUrl) || picker.dataset.url;
        if (!baseUrl) return null;
        const withQuickView = picker.quickView ? `${baseUrl}/gsc-quick-view` : baseUrl;
        let url;
        try {
            url = new URL(withQuickView, window.location.origin);
        } catch (error) {
            return null;
        }

        const sectionId = picker.dataset.sectionIdWithoutPrefix;
        if (sectionId) {
            url.searchParams.set('section_id', sectionId);
        }
        if (variantId) {
            url.searchParams.set('variant', String(variantId));
        }

        const optionGroups = picker.querySelectorAll('[data-variant-picker-option-inputs]');
        if (optionGroups.length > 0) {
            const optionValues = [];
            optionGroups.forEach((group) => {
                const input = group.querySelector('input:checked');
                const valueId = input && input.dataset ? input.dataset.valueId : null;
                if (valueId) optionValues.push(valueId);
            });
            if (optionValues.length > 0) {
                url.searchParams.set('option_values', optionValues.join(','));
            }
        }

        return url.toString();
    }

    function getOptionIndexFromInputName(name) {
        if (typeof name !== 'string') return null;
        const optionIndex = getOptionIndexFromName(name);
        if (optionIndex != null) return optionIndex;
        const match = name.match(/-(\d+)$/);
        if (!match) return null;
        const parsed = Number(match[1]);
        if (!Number.isFinite(parsed) || parsed < 1) return null;
        return parsed - 1;
    }

    function updateFormVariantId(form, variant) {
        if (!form || !variant) return;
        const idInput = form.querySelector('input[name="id"]');
        if (idInput) {
            idInput.value = String(variant.id);
        }
    }

    function syncFormOptionsToVariant(form, variant) {
        if (!form || !variant || !Array.isArray(variant.options)) return false;
        const formId = form.id ? escapeSelector(form.id) : null;
        const inputs = formId
            ? document.querySelectorAll(`input[form="${formId}"][type="radio"]`)
            : form.querySelectorAll('input[type="radio"]');
        const changedInputs = [];

        variant.options.forEach((value, index) => {
            if (value == null) return;
            inputs.forEach((input) => {
                const inputIndex = getOptionIndexFromInputName(input.name);
                if (inputIndex !== index) return;
                const shouldCheck = input.value === value;
                if (input.checked !== shouldCheck) {
                    input.checked = shouldCheck;
                    changedInputs.push(input);
                }
            });
        });

        if (changedInputs.length > 0) {
            syncingForms.add(form);
            changedInputs.forEach((input) => {
                input.dispatchEvent(new Event('change', { bubbles: true }));
            });
            setTimeout(() => syncingForms.delete(form), 0);
            return true;
        }

        return false;
    }

    function normalizeVariantOptions(variant) {
        if (!variant) return variant;
        if (!Array.isArray(variant.options)) {
            variant.options = [variant.option1, variant.option2, variant.option3].filter(
                (value) => value != null && value !== ''
            );
        }
        return variant;
    }

    function normalizeProductVariants(product) {
        if (!product || !Array.isArray(product.variants)) return product;
        product.variants = product.variants.map((variant) => normalizeVariantOptions(variant));
        return product;
    }

    function cacheAndNormalizeProduct(handle, data) {
        if (!data) return null;
        if (Array.isArray(data.variants)) {
            data.variants = data.variants.map((variant) => normalizeVariantOptions(variant));
        }
        productDataCache.set(handle, Promise.resolve(data));
        productDataResolved.set(handle, data);
        return data;
    }

    function getCachedProduct(handle) {
        if (!handle) return null;
        return productDataResolved.get(handle) || null;
    }

    function loadProductData(handle) {
        if (!handle) return Promise.resolve(null);
        if (productDataCache.has(handle)) {
            return productDataCache.get(handle);
        }
        const loader = (async () => {
            if (window.ProductLoader?.load) {
                try {
                    const loaded = await window.ProductLoader.load(handle);
                    return cacheAndNormalizeProduct(handle, loaded);
                } catch (error) {
                    console.warn('[kiks] ProductLoader failed, falling back to fetch', error);
                }
            }
            const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
            const response = await fetch(`${root}products/${handle}.js`);
            if (!response.ok) {
                throw new Error(`Failed to load product JSON for handle "${handle}"`);
            }
            const data = await response.json();
            return cacheAndNormalizeProduct(handle, data);
        })();
        productDataCache.set(handle, loader);
        return loader;
    }

    function getOptionIndexFromName(name) {
        if (typeof name !== 'string') return null;
        const match = name.match(/option(\d+)/i);
        if (!match) return null;
        const parsed = Number(match[1]);
        if (!Number.isFinite(parsed)) return null;
        return parsed - 1;
    }

    function cloneBaseOptions(baseVariant, product) {
        if (baseVariant && Array.isArray(baseVariant.options) && baseVariant.options.length) {
            return baseVariant.options.slice();
        }
        if (product && Array.isArray(product.options) && product.options.length) {
            return new Array(product.options.length).fill(null);
        }
        return [];
    }

    function findVariantMatchingOptions(product, desiredOptions) {
        if (!product || !Array.isArray(product.variants)) return null;
        return (
            product.variants.find((variant) => {
                if (!Array.isArray(variant.options)) return false;
                return desiredOptions.every((value, index) => {
                    if (value == null || value === '') return true;
                    return variant.options[index] === value;
                });
            }) || null
        );
    }

    function resolveVariantForOptionValue(product, baseVariant, optionIndex, optionValue) {
        if (optionIndex == null) return null;
        const desiredOptions = cloneBaseOptions(baseVariant, product);
        if (optionIndex >= desiredOptions.length) {
            desiredOptions.length = optionIndex + 1;
        }
        desiredOptions[optionIndex] = optionValue;
        return findVariantMatchingOptions(product, desiredOptions);
    }

    function resolveVariantForOptionValueFromOptions(product, baseOptions, optionIndex, optionValue) {
        if (optionIndex == null) return null;
        const desiredOptions = Array.isArray(baseOptions) ? baseOptions.slice() : [];
        if (optionIndex >= desiredOptions.length) {
            desiredOptions.length = optionIndex + 1;
        }
        desiredOptions[optionIndex] = optionValue;
        return findVariantMatchingOptions(product, desiredOptions);
    }

    function clearTargetCardDelta(deltaEl) {
        if (!deltaEl) return;
        deltaEl.dataset.deltaValue = '0';
        deltaEl.textContent = '';
        deltaEl.classList.remove('variant-target-card__delta--positive', 'variant-target-card__delta--negative');
    }

    function getVariantById(product, id) {
        if (!product || !Array.isArray(product.variants) || !id) return null;
        const numericId = Number(id);
        if (!Number.isFinite(numericId)) return null;
        return product.variants.find((variant) => Number(variant.id) === numericId) || null;
    }

    function getFormForInput(input) {
        if (!input) return null;
        if (input.form) return input.form;
        const formId = input.getAttribute('form');
        if (!formId) return null;
        return document.getElementById(formId);
    }

    function getInputByIdInContext(inputId, contextElement = null) {
        if (!inputId) return null;
        const escapedInputId = escapeSelector(inputId);

        const contextPicker = contextElement && contextElement.closest
            ? contextElement.closest('variant-picker')
            : null;

        const contextFormId = contextElement?.dataset?.kiksFormId;
        if (contextFormId) {
            const escapedContextFormId = escapeSelector(contextFormId);
            const byContextForm = document.querySelector(`input#${escapedInputId}[form="${escapedContextFormId}"]`);
            if (byContextForm) return byContextForm;
        }

        if (contextPicker) {
            const inPicker = contextPicker.querySelector(`#${escapedInputId}`);
            if (inPicker) return inPicker;

            const pickerFormId = contextPicker.getAttribute('form');
            if (pickerFormId) {
                const escapedFormId = escapeSelector(pickerFormId);
                const byForm = contextPicker.querySelector(`input#${escapedInputId}[form="${escapedFormId}"]`) ||
                    document.querySelector(`input#${escapedInputId}[form="${escapedFormId}"]`);
                if (byForm) return byForm;
            }
        }

        return document.getElementById(inputId);
    }

    function getInputByOptionPositionAndValue(picker, optionPosition, optionValue) {
        if (!picker) return null;
        const position = Number(optionPosition);
        if (!Number.isFinite(position) || position < 1) return null;
        if (optionValue == null || optionValue === '') return null;

        const escapedValue = escapeSelector(String(optionValue));
        const escapedPosition = escapeSelector(String(position));
        return picker.querySelector(`input[type="radio"][name$="-${escapedPosition}"][value="${escapedValue}"]`);
    }

    function getSelectedOptionValuesForPicker(picker) {
        if (!picker) return [];

        const handle = picker.getAttribute('handle') || picker.dataset.handle;
        const cachedProduct = getCachedProduct(handle);
        const pickerVariants = getPickerVariants(picker);

        let optionCount = cachedProduct?.options?.length || 0;
        if (optionCount === 0 && Array.isArray(pickerVariants?.[0]?.options)) {
            optionCount = pickerVariants[0].options.length;
        }

        const selections = optionCount > 0 ? new Array(optionCount).fill(null) : [];
        let maxSeenIndex = optionCount - 1;

        const assignSelection = (index, value) => {
            if (!Number.isFinite(index) || index < 0 || value == null || value === '') return;
            if (index > maxSeenIndex) {
                maxSeenIndex = index;
            }
            while (selections.length <= index) {
                selections.push(null);
            }
            selections[index] = value;
        };

        const directGroups = picker.querySelectorAll('[data-variant-picker-option-inputs]');
        directGroups.forEach((group, groupIndex) => {
            const checked = group.querySelector('input[type="radio"]:checked') ||
                group.querySelector('input[type="hidden"][name], select[name]');
            if (!checked) return;
            const parsedIndex = getOptionIndexFromInputName(checked.name);
            assignSelection(parsedIndex != null ? parsedIndex : groupIndex, checked.value);
        });

        const selectors = picker.querySelectorAll('[data-option-selector]');
        selectors.forEach((selector, selectorIndex) => {
            const input = selector.querySelector(
                'input[name]:checked, select[name], input[name][type="hidden"]'
            );
            if (!input) return;
            const parsedIndex = getOptionIndexFromInputName(input.name);
            assignSelection(parsedIndex != null ? parsedIndex : selectorIndex, input.value);
        });

        const formId = picker.getAttribute('form');
        let form = null;
        if (formId) {
            form = document.forms[formId] || document.getElementById(formId);
        }
        if (!form) {
            form = picker.querySelector('form');
        }

        if (form) {
            const scopedInputs = Array.from(form.querySelectorAll('input[name]:checked, select[name]'));
            const escapedFormId = form.id ? escapeSelector(form.id) : null;
            const formAttrInputs = escapedFormId
                ? Array.from(document.querySelectorAll(`input[form="${escapedFormId}"][name]:checked, select[form="${escapedFormId}"][name]`))
                : [];

            scopedInputs.concat(formAttrInputs).forEach((input) => {
                const optionIndex = getOptionIndexFromInputName(input.name);
                assignSelection(optionIndex, input.value);
            });
        }

        if (optionCount === 0 && maxSeenIndex >= 0) {
            while (selections.length <= maxSeenIndex) {
                selections.push(null);
            }
        }

        return selections;
    }

    function findVariantBySelections(product, selections) {
        if (!product || !Array.isArray(product.variants)) return null;
        return (
            product.variants.find((variant) => {
                if (!Array.isArray(variant.options)) return false;
                return selections.every((value, index) => {
                    if (value == null || value === '') return true;
                    return variant.options[index] === value;
                });
            }) || null
        );
    }

    function normalizeSelectionsForProduct(product, selections) {
        const normalizedSelections = Array.isArray(selections) ? selections.slice() : [];
        const optionCount = product?.options?.length || 0;
        if (optionCount <= 0) return normalizedSelections;

        while (normalizedSelections.length < optionCount) {
            normalizedSelections.push(null);
        }
        if (normalizedSelections.length > optionCount) {
            normalizedSelections.length = optionCount;
        }
        return normalizedSelections;
    }

    function buildOptionValueRankMaps(product) {
        if (!product || !Array.isArray(product.variants)) return [];
        const rankMaps = [];

        product.variants.forEach((variant) => {
            if (!Array.isArray(variant.options)) return;
            variant.options.forEach((value, index) => {
                if (value == null || value === '') return;
                if (!rankMaps[index]) {
                    rankMaps[index] = new Map();
                }
                if (!rankMaps[index].has(value)) {
                    rankMaps[index].set(value, rankMaps[index].size);
                }
            });
        });

        return rankMaps;
    }

    function findClosestVariantForSelections(product, selections, pinnedOptionIndex = null) {
        if (!product || !Array.isArray(product.variants) || product.variants.length === 0) return null;

        const normalizedSelections = normalizeSelectionsForProduct(product, selections);
        const pinnedValue = pinnedOptionIndex != null ? normalizedSelections[pinnedOptionIndex] : null;
        const hasPinnedValue = pinnedOptionIndex != null && pinnedValue != null && pinnedValue !== '';
        const optionValueRanks = buildOptionValueRankMaps(product);

        let bestVariant = null;
        let bestScore = null;

        product.variants.forEach((variant, variantOrder) => {
            if (!variant || !Array.isArray(variant.options)) return;
            if (hasPinnedValue && variant.options[pinnedOptionIndex] !== pinnedValue) return;

            let exactMatchCount = 0;
            let distancePenalty = 0;

            normalizedSelections.forEach((selectedValue, index) => {
                if (selectedValue == null || selectedValue === '') return;

                const variantValue = variant.options[index];
                if (variantValue === selectedValue) {
                    exactMatchCount += 1;
                    return;
                }

                const rankMap = optionValueRanks[index];
                const selectedRank = rankMap ? rankMap.get(selectedValue) : null;
                const variantRank = rankMap ? rankMap.get(variantValue) : null;
                if (Number.isFinite(selectedRank) && Number.isFinite(variantRank)) {
                    distancePenalty += Math.abs(variantRank - selectedRank);
                } else {
                    distancePenalty += 100;
                }
            });

            const score = {
                exactMatchCount,
                distancePenalty,
                available: variant.available ? 1 : 0,
                order: variantOrder,
            };

            const isBetter =
                !bestScore ||
                score.exactMatchCount > bestScore.exactMatchCount ||
                (score.exactMatchCount === bestScore.exactMatchCount &&
                    score.distancePenalty < bestScore.distancePenalty) ||
                (score.exactMatchCount === bestScore.exactMatchCount &&
                    score.distancePenalty === bestScore.distancePenalty &&
                    score.available > bestScore.available) ||
                (score.exactMatchCount === bestScore.exactMatchCount &&
                    score.distancePenalty === bestScore.distancePenalty &&
                    score.available === bestScore.available &&
                    score.order < bestScore.order);

            if (isBetter) {
                bestVariant = variant;
                bestScore = score;
            }
        });

        if (bestVariant) return bestVariant;

        if (hasPinnedValue) return null;
        return product.variants.find((variant) => variant?.available) || product.variants[0] || null;
    }

    function resolvePricingState(product, selections, input = null) {
        const normalizedProduct = normalizeProductVariants(product);
        if (!normalizedProduct) {
            return {
                product: null,
                selections: [],
                activeSelections: [],
                exactVariant: null,
                activeVariant: null,
                normalizedToClosest: false,
            };
        }

        const normalizedSelections = normalizeSelectionsForProduct(normalizedProduct, selections);
        const exactVariant = findVariantBySelections(normalizedProduct, normalizedSelections);

        let activeVariant = exactVariant;
        let activeSelections = normalizedSelections;
        let normalizedToClosest = false;

        if (!activeVariant) {
            const pinnedOptionIndex = input ? getOptionIndexFromInputName(input.name) : null;
            activeVariant = findClosestVariantForSelections(
                normalizedProduct,
                normalizedSelections,
                pinnedOptionIndex
            );
            normalizedToClosest = Boolean(activeVariant);
        }

        if (activeVariant) {
            normalizeVariantOptions(activeVariant);
            activeSelections = normalizeSelectionsForProduct(normalizedProduct, activeVariant.options);
        }

        return {
            product: normalizedProduct,
            selections: normalizedSelections,
            activeSelections,
            exactVariant,
            activeVariant,
            normalizedToClosest,
        };
    }

    // ============================================================================
    // Kiks Sale Timer Component
    // ============================================================================

    class KiksSaleTimer extends HTMLElement {
        static get observedAttributes() {
            return ['data-end-time'];
        }

        constructor() {
            super();
            this._intervalId = null;
        }

        connectedCallback() {
            if (this.dataset.kiksSaleTimerInitialized === 'true') return;
            this.dataset.kiksSaleTimerInitialized = 'true';
            this._init();
        }

        disconnectedCallback() {
            this._stop();
            this.dataset.kiksSaleTimerInitialized = '';
        }

        attributeChangedCallback() {
            if (this.dataset.kiksSaleTimerInitialized === 'true') {
                this._stop();
                this._init();
            }
        }

        _pad(value) {
            return String(value).padStart(2, '0');
        }

        _parseEndDate(value) {
            if (!value) return null;
            let parsed = Date.parse(value);
            if (!Number.isNaN(parsed)) return parsed;
            parsed = Date.parse(value.replace(' ', 'T'));
            return Number.isNaN(parsed) ? null : parsed;
        }

        _stop() {
            if (this._intervalId) {
                clearInterval(this._intervalId);
                this._intervalId = null;
            }
        }

        _showMessage(type) {
            const countdown = this.querySelector('[data-countdown]');
            const expiredMessage = this.querySelector('[data-expired-message]');
            const fallbackMessage = this.querySelector('[data-fallback-message]');

            if (countdown) countdown.hidden = true;

            if (type === 'expired') {
                if (expiredMessage) {
                    expiredMessage.hidden = false;
                    expiredMessage.textContent =
                        this.dataset.expiredText || expiredMessage.textContent;
                }
                this.classList.add('kiks-sale-timer--expired');
            } else {
                if (fallbackMessage) {
                    fallbackMessage.hidden = false;
                    fallbackMessage.textContent =
                        this.dataset.noCountdownText || fallbackMessage.textContent;
                }
                this.classList.add('kiks-sale-timer--static');
            }
        }

        _updateCountdownValues(remainingMs) {
            const countdown = this.querySelector('[data-countdown]');
            if (!countdown) return;

            countdown.hidden = false;
            this.classList.remove('kiks-sale-timer--expired', 'kiks-sale-timer--static');

            const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000));
            const days = Math.floor(totalSeconds / 86400);
            const hours = Math.floor((totalSeconds % 86400) / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            const seconds = totalSeconds % 60;

            const valueMap = {
                days: days > 99 ? String(days) : this._pad(days),
                hours: this._pad(hours),
                minutes: this._pad(minutes),
                seconds: this._pad(seconds),
            };

            Object.keys(valueMap).forEach((unit) => {
                const node = this.querySelector(`[data-value="${unit}"]`);
                if (node) node.textContent = valueMap[unit];
            });
        }

        _init() {
            const endDateValue = (this.dataset.endTime || '').trim();
            const endTimestamp = this._parseEndDate(endDateValue);

            if (!endTimestamp) {
                this._showMessage('static');
                return;
            }

            const update = () => {
                const now = Date.now();
                const remaining = endTimestamp - now;

                if (remaining <= 0) {
                    this._stop();
                    this._showMessage('expired');
                    return;
                }

                this._updateCountdownValues(remaining);
            };

            update();
            this._intervalId = setInterval(update, 1000);
        }
    }

    // ============================================================================
    // Kiks Size Card Component
    // ============================================================================

    class KiksSizeCard extends HTMLElement {
        constructor() {
            super();
            this._input = null;
            this._onChange = this._onChange.bind(this);
            this._onClick = this._onClick.bind(this);
        }

        connectedCallback() {
            this._input = this._getInput();
            this.addEventListener('click', this._onClick);
            if (this._input) {
                this._input.addEventListener('change', this._onChange);
                this._notifyPricingManager();
            }
        }

        disconnectedCallback() {
            if (this._input) {
                this._input.removeEventListener('change', this._onChange);
            }
            this.removeEventListener('click', this._onClick);
        }

        _getInput() {
            const inputId = this.getAttribute('for');
            if (inputId) {
                const byId = getInputByIdInContext(inputId, this);
                if (byId) return byId;
            }
            const picker = this.closest('variant-picker');
            const byPositionAndValue = getInputByOptionPositionAndValue(picker, this.dataset.sizePosition, this.dataset.sizeValue);
            if (byPositionAndValue) return byPositionAndValue;
            return this.querySelector('[data-kiks-size-input]');
        }

        _onClick(event) {
            this._bindInputIfAvailable();
            const infoOrSidebarTrigger = event.target.closest &&
                event.target.closest('[data-kiks-size-info], [data-sidebar-button]');
            if (infoOrSidebarTrigger && this.contains(infoOrSidebarTrigger)) {
                return;
            }
            if (!this._input || this.hasAttribute('data-unavailable')) {
                return;
            }
            if (this._input.disabled) return;
            if (this._input.checked) return;
            event.preventDefault();
            this._input.checked = true;
            // Optimistically reflect selection immediately; async refresh can follow.
            this._updateSelectedState();
            this._input.dispatchEvent(new Event('change', { bubbles: true }));
        }

        _onChange() {
            this._bindInputIfAvailable();
            this._updateSelectedState();
            this._notifyPricingManager();
        }

        _bindInputIfAvailable() {
            const nextInput = this._getInput();
            if (!nextInput) return false;
            if (this._input === nextInput) return true;

            if (this._input) {
                this._input.removeEventListener('change', this._onChange);
            }

            this._input = nextInput;
            this._input.addEventListener('change', this._onChange);
            return true;
        }

        _updateSelectedState() {
            if (!this._input) return;
            const selector = `[data-kiks-size-card][data-size-name="${escapeSelector(this._input.name)}"]`;
            const scope = this.closest('variant-picker') || document;
            scope.querySelectorAll(selector).forEach((card) => {
                const cardInput = card.querySelector('[data-kiks-size-input]') ||
                    (card.getAttribute('for') ? getInputByIdInContext(card.getAttribute('for'), card) : null);
                card.classList.toggle('is-selected', !!cardInput && cardInput.checked);
            });
        }

        _notifyPricingManager() {
            this._bindInputIfAvailable();
            if (!this._input) return;
            const form = getFormForInput(this._input);
            const pricingManager = form?.closest('kiks-product-pricing') ||
                document.querySelector('kiks-product-pricing');
            if (pricingManager) {
                pricingManager.refreshPricing(form, this._input);
            }
        }

        updatePricing(variant) {
            if (!variant) return;

            const priceValue = normalizeMoneyValue(variant.price);
            if (priceValue != null) {
                const priceEl = this.querySelector('.variant-size-card__price');
                if (priceEl) {
                    priceEl.textContent = formatMoney(priceValue);
                }
            }

            const compareAtValue = normalizeMoneyValue(variant.compare_at_price);
            let referencePrice = Number.isFinite(priceValue) ? priceValue : null;
            if (Number.isFinite(compareAtValue) && (referencePrice == null || compareAtValue > referencePrice)) {
                referencePrice = compareAtValue;
            }

            if (referencePrice != null) {
                const totalValueEl = this.querySelector('.variant-size-card__total-value');
                if (totalValueEl) {
                    totalValueEl.textContent = `(Total Value ${formatMoney(referencePrice)})`;
                }
            }
        }

        getOptionIndex() {
            if (!this._input) {
                this._input = this._getInput();
            }
            if (this._input) {
                return getOptionIndexFromInputName(this._input.name);
            }
            const position = Number(this.dataset.sizePosition);
            if (Number.isFinite(position) && position > 0) {
                return position - 1;
            }
            return getOptionIndexFromInputName(this.dataset.sizeName || '');
        }

        getOptionValue() {
            return this.dataset.sizeValue || (this._input ? this._input.value : null);
        }
    }

    // ============================================================================
    // Kiks Target Card Component
    // ============================================================================

    class KiksTargetCard extends HTMLElement {
        constructor() {
            super();
            this._input = null;
            this._onChange = this._onChange.bind(this);
            this._onClick = this._onClick.bind(this);
        }

        connectedCallback() {
            this._input = this._getInput();
            this.addEventListener('click', this._onClick);
            if (this._input) {
                this._input.addEventListener('change', this._onChange);
                this._updateGroup();
                this._notifyPricingManager();
            }
        }

        disconnectedCallback() {
            if (this._input) {
                this._input.removeEventListener('change', this._onChange);
            }
            this.removeEventListener('click', this._onClick);
        }

        _getInput() {
            const inputId = this.getAttribute('for');
            if (inputId) {
                const byId = getInputByIdInContext(inputId, this);
                if (byId) return byId;
            }
            const picker = this.closest('variant-picker');
            const byPositionAndValue = getInputByOptionPositionAndValue(picker, this.dataset.targetPosition, this.dataset.targetValue);
            if (byPositionAndValue) return byPositionAndValue;
            return this.querySelector('input[type="radio"]');
        }

        _onClick(event) {
            this._bindInputIfAvailable();
            if (!this._input || this.hasAttribute('data-unavailable')) {
                return;
            }
            if (this._input.disabled) return;
            if (this._input.checked) return;
            event.preventDefault();
            this._input.checked = true;
            // Optimistically reflect selection immediately; async refresh can follow.
            this._updateGroup();
            this._input.dispatchEvent(new Event('change', { bubbles: true }));
        }

        _onChange() {
            this._bindInputIfAvailable();
            this._updateGroup();
            this._notifyPricingManager();
        }

        _bindInputIfAvailable() {
            const nextInput = this._getInput();
            if (!nextInput) return false;
            if (this._input === nextInput) return true;

            if (this._input) {
                this._input.removeEventListener('change', this._onChange);
            }

            this._input = nextInput;
            this._input.addEventListener('change', this._onChange);
            return true;
        }

        _updateGroup(basePriceOverride = null) {
            if (!this._input || !this._input.name) return;
            const selector = `[data-kiks-target-card][data-target-name="${escapeSelector(this._input.name)}"]`;
            const scope = this.closest('variant-picker') || document;

            // Use provided base price (current variant price) if available, otherwise try to calculate it
            let basePrice = basePriceOverride;

            if (basePrice == null) {
                // Try to get the current variant price (includes all selected options) as the base price
                // This ensures deltas are calculated correctly relative to the full current selection
                const form = getFormForInput(this._input);
                if (form) {
                    // Try to get the variant picker for this form
                    const VARIANT_PICKER_SELECTOR = 'variant-picker';
                    let picker = form.closest(VARIANT_PICKER_SELECTOR);
                    if (!picker && form.id) {
                        const escapedFormId = escapeSelector(form.id);
                        picker = document.querySelector(`${VARIANT_PICKER_SELECTOR}[form="${escapedFormId}"]`);
                    }

                    if (picker) {
                        // Get current selections and find the matching variant
                        const selections = getSelectedOptionValuesForPicker(picker);
                        const handle = picker.getAttribute('handle') || picker.dataset.handle;
                        const product = getCachedProduct(handle);
                        if (product) {
                            const optionCount = product.options?.length || 0;
                            while (selections.length < optionCount) {
                                selections.push(null);
                            }
                            const currentVariant = findVariantBySelections(product, selections);
                            if (currentVariant) {
                                basePrice = normalizeMoneyValue(currentVariant.price);
                                if (basePrice == null) basePrice = null;
                            }
                        }
                    }
                }
            }

            // If no base price found, skip updating deltas but still update selected state
            const shouldUpdateDeltas = basePrice != null;

            scope.querySelectorAll(selector).forEach((card) => {
                const cardInput = card._getInput ? card._getInput() :
                    (card.getAttribute('for') ? getInputByIdInContext(card.getAttribute('for'), card) :
                        card.querySelector('input[type="radio"]'));
                const isSelected = !!cardInput && cardInput.checked;
                card.classList.toggle('is-selected', isSelected);
                const innerLabel = card.querySelector('.variant-target-card');
                if (innerLabel) innerLabel.classList.toggle('is-selected', isSelected);
                const deltaEl = card.querySelector('.variant-target-card__delta');
                const cardPrice = this._getPrice(card);
                if (!deltaEl) return;
                // No exact selected variant: clear stale delta values.
                if (!shouldUpdateDeltas || cardPrice == null) {
                    clearTargetCardDelta(deltaEl);
                    return;
                }

                // Calculate savings: positive = you save money (cheaper), negative = you pay more (expensive)
                const savings = basePrice - cardPrice;
                if (savings === 0 || savings > 0) {
                    clearTargetCardDelta(deltaEl);
                } else {
                    const formatted = formatMoneyWithoutCents(Math.abs(savings));
                    deltaEl.dataset.deltaValue = String(savings);
                    deltaEl.textContent = `+${formatted}`;
                }
                deltaEl.classList.toggle('variant-target-card__delta--positive', savings < 0);
                deltaEl.classList.remove('variant-target-card__delta--negative');
            });
        }

        _getCardFromInput(input) {
            if (!input) return null;
            const escapedId = escapeSelector(input.id);
            return document.querySelector(`[data-kiks-target-card][data-target-input="${escapedId}"]`);
        }

        _getPrice(card) {
            if (!card) return null;
            return normalizeMoneyValue(card.dataset.targetPrice);
        }

        _notifyPricingManager() {
            this._bindInputIfAvailable();
            if (!this._input) return;
            const form = getFormForInput(this._input);
            const pricingManager = form?.closest('kiks-product-pricing') ||
                document.querySelector('kiks-product-pricing');
            if (pricingManager) {
                pricingManager.refreshPricing(form, this._input);
            }
        }

        updatePricing(variant, basePrice) {
            if (!variant) return;

            const cardPrice = normalizeMoneyValue(variant.price);
            if (cardPrice == null) return;

            this.dataset.targetPrice = String(cardPrice);

            const deltaEl = this.querySelector('.variant-target-card__delta');
            if (!deltaEl) return;

            // Calculate savings: positive = you save money (cheaper), negative = you pay more (expensive)
            const savings = basePrice - cardPrice;
            const absoluteSavings = Math.abs(savings);
            const formattedSavings = formatMoneyWithoutCents(absoluteSavings);

            if (savings === 0 || savings > 0) {
                deltaEl.textContent = '';
                deltaEl.dataset.deltaValue = '0';
                deltaEl.classList.remove('variant-target-card__delta--positive', 'variant-target-card__delta--negative');
            } else {
                deltaEl.textContent = `+${formattedSavings}`;
                deltaEl.dataset.deltaValue = String(savings);
                deltaEl.classList.add('variant-target-card__delta--positive');
                deltaEl.classList.remove('variant-target-card__delta--negative');
            }
        }

        updateAvailability(isAvailable, isSelectable = true) {
            // Block/unblock pointer events only for non-existent combinations.
            if (isSelectable) {
                this.removeAttribute('data-unavailable');
            } else {
                this.setAttribute('data-unavailable', '');
            }

            // Update the inner label classes and attributes
            const label = this.querySelector('.variant-target-card');
            if (label) {
                label.classList.toggle('variant-target-card--unavailable', !isSelectable);
                if (isSelectable) {
                    label.removeAttribute('data-no-available');
                } else {
                    label.setAttribute('data-no-available', '');
                }
            }

            // Add or remove the "Sold Out" badge
            let badge = this.querySelector('.variant-target-card__sold-out-badge');
            const mode = (this.dataset.mode || '').toLowerCase();
            if (mode === 'simple') {
                if (badge) {
                    badge.remove();
                }
            } else if (isSelectable && !isAvailable) {
                if (!badge && label) {
                    badge = document.createElement('span');
                    badge.className = 'variant-target-card__sold-out-badge';
                    badge.textContent = 'Sold Out';
                    const cornerBadge = label.querySelector('.variant-target-card__corner-badge');
                    if (cornerBadge) {
                        cornerBadge.after(badge);
                    } else {
                        label.prepend(badge);
                    }
                }
            } else if (badge) {
                badge.remove();
            }

            // Keep the hidden radio input in sync
            if (this._input) {
                this._input.disabled = !isSelectable;
            }
        }

        getOptionIndex() {
            if (!this._input) {
                this._input = this._getInput();
            }
            if (this._input) {
                return getOptionIndexFromInputName(this._input.name);
            }
            const position = Number(this.dataset.targetPosition);
            if (Number.isFinite(position) && position > 0) {
                return position - 1;
            }
            return getOptionIndexFromInputName(this.dataset.targetName || '');
        }

        getOptionValue() {
            return this.dataset.targetValue || (this._input ? this._input.value : null);
        }
    }


    // ============================================================================
    // Kiks Product Pricing Manager Component
    // ============================================================================

    class KiksProductPricing extends HTMLElement {
        constructor() {
            super();
            this._selectedPillIds = new Set();
            this._onVariantChange = this._onVariantChange.bind(this);
            this._onOptionChange = this._onOptionChange.bind(this);
            this._onDropdownClick = this._onDropdownClick.bind(this);
            this._onPillClick = this._onPillClick.bind(this);
        }

        connectedCallback() {
            document.addEventListener('variant:change', this._onVariantChange);
            document.addEventListener('change', this._onOptionChange);
            document.addEventListener('click', this._onDropdownClick);
            document.addEventListener('click', this._onPillClick);
            this._refreshAll();
        }

        disconnectedCallback() {
            document.removeEventListener('variant:change', this._onVariantChange);
            document.removeEventListener('change', this._onOptionChange);
            document.removeEventListener('click', this._onDropdownClick);
            document.removeEventListener('click', this._onPillClick);
            if (this._pillObserver) {
                this._pillObserver.disconnect();
                this._pillObserver = null;
            }
        }

        _onVariantChange(event) {
            const container = event.target;
            if (!container) return;

            const { form, picker } = this._resolveFormAndPicker(container);
            if (!form || !picker) return;

            const product = normalizeProductVariants(event.detail?.product);
            const variant = event.detail?.variant ? normalizeVariantOptions(event.detail.variant) : null;

            if (product && variant) {
                const state = {
                    product,
                    selections: normalizeSelectionsForProduct(product, variant.options),
                    activeSelections: normalizeSelectionsForProduct(product, variant.options),
                    exactVariant: variant,
                    activeVariant: variant,
                    normalizedToClosest: false,
                };
                this._applyPricingState(form, picker, state, { syncForm: false });
                return;
            }

            const triggerInput = form.querySelector('input[name^="option"]:checked') ||
                picker.querySelector('input[name^="option"]:checked');
            this.refreshPricing(form, triggerInput);
        }

        _onOptionChange(event) {
            const target = event.target;
            if (!target || !target.name) return;
            // Support both theme "option1/option2" and "OptionName-Position" (e.g. Size-1, Thickness-2)
            const isOptionInput = /^option\d+/i.test(target.name) || /^.+-\d+$/.test(target.name);
            if (!isOptionInput) return;
            const form = getFormForInput(target);
            if (form && syncingForms.has(form)) return;
            const picker = this._getVariantPickerFromInput(target) || (form ? this._getVariantPickerForForm(form) : null);
            let isHandledByCardComponent = false;
            if (target.id) {
                const escapedTargetId = escapeSelector(target.id);
                isHandledByCardComponent = Boolean(
                    document.querySelector(`[data-kiks-target-card][for="${escapedTargetId}"], [data-kiks-size-card][for="${escapedTargetId}"]`)
                );
            }
            if (picker) this._updateButtonPills(picker, true);
            if (form && picker) {
                this._applyOptimisticPickerState(form, picker, target);
            }
            if (form) {
                const existingTimer = deferredRefreshTimers.get(form);
                if (existingTimer) {
                    clearTimeout(existingTimer);
                }
                const timer = setTimeout(() => {
                    deferredRefreshTimers.delete(form);
                    if (!isHandledByCardComponent) {
                        this.refreshPricing(form, target);
                    }
                }, 0);
                deferredRefreshTimers.set(form, timer);
            }
        }

        _onDropdownClick(event) {
            const sizeToggle = event.target?.closest?.('.variant-picker-dropdown__toggle--size-content');
            if (sizeToggle) {
                const picker = sizeToggle.closest('variant-picker');
                if (picker) {
                    const formId = picker.getAttribute('form');
                    const form = formId ? (document.forms[formId] || document.getElementById(formId)) : picker.querySelector('form');
                    requestAnimationFrame(() => {
                        const handle = picker.getAttribute('handle') || picker.dataset.handle;
                        const product = getCachedProduct(handle);
                        const selections = getSelectedOptionValuesForPicker(picker);
                        this._updateSizeDropdownItems(picker, form || null, product, selections);
                        this._syncSizeDropdownItemStates(picker, form || null);
                    });
                }
                return;
            }

            const item = event.target?.closest?.('.variant-picker-dropdown__item');
            if (!item) return;
            const infoTrigger = event.target?.closest?.('[data-kiks-size-info]');
            if (infoTrigger && item.contains(infoTrigger)) return;
            const inputId = item.getAttribute('for');
            if (!inputId) return;
            const picker = this._resolvePickerFromDropdownItem(item);
            const input = getInputByOptionPositionAndValue(
                picker,
                item.dataset.kiksOptionPosition,
                item.dataset.kiksOptionValue
            ) || getInputByIdInContext(inputId, picker || item);
            if (!input) return;
            if (input.disabled) return;
            if (input.checked) return;
            if (item.classList.contains('variant-picker-dropdown__item--size')) {
                this._syncSizeDropdownToggleFromItem(item);
                this._closeDropdownForItem(item);
            }
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
            const form = getFormForInput(input);
            const resolvedPicker = picker || this._getVariantPickerFromInput(input) || (form ? this._getVariantPickerForForm(form) : null);
            if (form && resolvedPicker) {
                this._applyOptimisticPickerState(form, resolvedPicker, input);
                const handle = resolvedPicker.getAttribute('handle') || resolvedPicker.dataset.handle;
                const product = getCachedProduct(handle);
                const selections = getSelectedOptionValuesForPicker(resolvedPicker);
                this._updateSizeDropdownItems(resolvedPicker, form, product, selections);
                this._syncSizeDropdownItemStates(resolvedPicker, form);
            }
        }

        _resolvePickerFromDropdownItem(item) {
            if (!item) return null;

            const explicitPickerId = item.dataset.kiksPickerId;
            if (explicitPickerId) {
                const pickerById = document.getElementById(explicitPickerId);
                if (pickerById && pickerById.matches && pickerById.matches('variant-picker')) {
                    return pickerById;
                }
            }

            const explicitFormId = item.dataset.kiksFormId;
            if (explicitFormId) {
                const escapedFormId = escapeSelector(explicitFormId);
                const pickerByForm = document.querySelector(`variant-picker[form="${escapedFormId}"]`);
                if (pickerByForm) return pickerByForm;
            }

            const nearestPicker = item.closest('variant-picker');
            if (nearestPicker) return nearestPicker;

            const floatElement = item.closest('float-element');
            const floatId = floatElement?.id;
            if (!floatId) return null;

            const opener = document.querySelector(`[data-float-element-id="${escapeSelector(floatId)}"]`);
            if (!opener) return null;

            return opener.closest('variant-picker');
        }

        _closeDropdownForItem(item) {
            if (!item) return;
            const floatElement = item.closest('float-element');
            const floatId = floatElement?.id;
            if (!floatId) return;
            const escapedFloatId = escapeSelector(floatId);

            if (floatElement) {
                if (window.customElements?.upgrade) {
                    window.customElements.upgrade(floatElement);
                }
                if (typeof floatElement.hide === 'function') {
                    floatElement.hide();
                } else {
                    floatElement.setAttribute('aria-hidden', 'true');
                }
            }

            const opener = document.querySelector(`[data-float-element-id="${escapedFloatId}"]`);
            if (opener) {
                if (typeof opener.setExpand === 'function') {
                    opener.setExpand(false);
                } else {
                    opener.setAttribute('data-aria-expanded', 'false');
                }
            }
        }

        _onPillClick(event) {
            const pill = event.target?.closest?.('.kik-variant-button-pill');
            if (!pill) return;
            const forId = pill.getAttribute('for');
            if (!forId) return;
            const picker = pill.closest('variant-picker');
            if (!picker) return;
            const clickedInput = getInputByIdInContext(forId, picker);
            if (!clickedInput) return;
            const isCurrentlySelected = this._selectedPillIds.has(forId);
            // Deselect all pills in the same option group first
            picker.querySelectorAll('.kik-variant-button-pill').forEach((p) => {
                const id = p.getAttribute('for');
                const input = id ? getInputByIdInContext(id, picker) : null;
                if (input && input.name === clickedInput.name) {
                    this._selectedPillIds.delete(id);
                    p.classList.remove('is-selected');
                }
            });
            // Select clicked pill only if it wasn't already selected (toggle off support)
            if (!isCurrentlySelected) {
                this._selectedPillIds.add(forId);
                pill.classList.add('is-selected');
            }
        }

        _applyPillSelected() {
            document.querySelectorAll('.kik-variant-button-pill').forEach((pill) => {
                const id = pill.getAttribute('for');
                if (id) pill.classList.toggle('is-selected', this._selectedPillIds.has(id));
            });
        }

        _setupPillObserver() {
            if (this._pillObserver) return;
            this._pillObserver = new MutationObserver((mutations) => {
                for (const m of mutations) {
                    if (m.type !== 'childList') continue;
                    for (const node of m.addedNodes) {
                        if (node.nodeType !== 1) continue;
                        if (
                            node.classList?.contains('kik-variant-button-pill') ||
                            node.querySelector?.('.kik-variant-button-pill')
                        ) {
                            this._applyPillSelected();
                            return;
                        }
                    }
                }
            });
            this._pillObserver.observe(document.body, { childList: true, subtree: true });
        }

        _updateButtonPills(picker, userInitiated = false) {
            if (!picker || !userInitiated) return;
            picker.querySelectorAll('.kik-variant-button-pill').forEach((pill) => {
                const id = pill.getAttribute('for');
                if (id) pill.classList.toggle('is-selected', this._selectedPillIds.has(id));
            });
        }

        _syncSizeDropdownToggleFromItem(item) {
            if (!item) return;

            const dropdownBody = item.closest('.variant-picker-dropdown__body--size-content');
            if (dropdownBody) {
                dropdownBody.querySelectorAll('.variant-picker-dropdown__item--size').forEach((entry) => {
                    entry.classList.toggle('selected', entry === item);
                });
            }

            const floatElement = item.closest('float-element');
            const floatId = floatElement?.id;
            if (!floatId) return;

            const opener = document.querySelector(`[data-float-element-id="${escapeSelector(floatId)}"]`);
            const toggleLabel = opener?.querySelector('[data-dropdown-variant-picker-label]');
            if (!toggleLabel) return;

            const selectedLabel = item.querySelector('.variant-size-card__label');
            const selectedInfo = item.querySelector('.variant-size-card__size-info');
            const selectedPrice = item.querySelector('.variant-size-card__price');
            const selectedTotal = item.querySelector('.variant-size-card__total-value');

            const toggleName = toggleLabel.querySelector('.variant-size-card__label');
            if (toggleName && selectedLabel) {
                toggleName.textContent = selectedLabel.textContent.trim();
            }

            const toggleInfo = toggleLabel.querySelector('.variant-size-card__size-info');
            if (toggleInfo) {
                if (selectedInfo && selectedInfo.textContent.trim() !== '') {
                    toggleInfo.textContent = selectedInfo.textContent.trim();
                    toggleInfo.style.removeProperty('display');
                } else {
                    toggleInfo.textContent = '';
                    toggleInfo.style.display = 'none';
                }
            }

            const togglePrice = toggleLabel.querySelector('.variant-size-card__price');
            if (togglePrice && selectedPrice) {
                togglePrice.textContent = selectedPrice.textContent.trim();
                if (selectedPrice.dataset.kiksCurrentPrice !== undefined) {
                    togglePrice.dataset.kiksCurrentPrice = selectedPrice.dataset.kiksCurrentPrice;
                }
                togglePrice.style.removeProperty('display');
            }

            const toggleTotal = toggleLabel.querySelector('.variant-size-card__total-value');
            if (toggleTotal) {
                if (selectedTotal) {
                    toggleTotal.textContent = selectedTotal.textContent.trim();
                    if (selectedTotal.dataset.kiksComparePrice !== undefined) {
                        toggleTotal.dataset.kiksComparePrice = selectedTotal.dataset.kiksComparePrice;
                    }
                    toggleTotal.style.removeProperty('display');
                } else {
                    toggleTotal.textContent = '';
                }
            }
        }

        _applyOptimisticPickerState(form, picker, input = null) {
            if (!form || !picker) return false;

            const variants = getPickerVariants(picker);
            if (!Array.isArray(variants) || variants.length === 0) return false;

            const optionCountFromVariants = Array.isArray(variants[0]?.options) ? variants[0].options.length : 0;
            const optionCount = optionCountFromVariants || picker.querySelectorAll('[data-variant-picker-option-inputs]').length || 0;
            const selections = getSelectedOptionValuesForPicker(picker);
            while (selections.length < optionCount) {
                selections.push(null);
            }

            const optimisticProduct = {
                variants: variants.map((variant) => normalizeVariantOptions(variant)),
                options: optionCount > 0 ? new Array(optionCount).fill('') : [],
            };
            const state = resolvePricingState(optimisticProduct, selections, input);
            if (!state.activeVariant) return false;

            this._applyPricingState(form, picker, state, { syncForm: true });
            return true;
        }

        _resolveFormAndPicker(container) {
            let form = null;
            let picker = null;

            if (container instanceof Element) {
                if (container.matches('variant-picker')) {
                    picker = container;
                } else if (container.closest) {
                    picker = container.closest('variant-picker');
                }

                if (container.matches('form')) {
                    form = container;
                } else if (container.closest) {
                    form = container.closest('form');
                }

                if (!form && container.matches('input, select, textarea')) {
                    form = getFormForInput(container);
                }

                if (!form && container.querySelector) {
                    const nestedInput = container.querySelector('input[name^="option"], input[name="id"]');
                    if (nestedInput) {
                        form = getFormForInput(nestedInput);
                    }
                }
            }

            if (!picker && form) {
                picker = this._getVariantPickerForForm(form);
            }

            if (!form && picker) {
                const formId = picker.getAttribute('form');
                if (formId) {
                    form = document.forms[formId] || document.getElementById(formId);
                }
                if (!form) {
                    form = picker.querySelector('form');
                }
            }

            return { form, picker };
        }

        _applyPricingState(form, picker, state, { syncForm = false } = {}) {
            const product = state?.product;
            const activeVariant = state?.activeVariant;
            const exactVariant = state?.exactVariant || null;
            const activeSelections = state?.activeSelections || [];
            const normalizedToClosest = Boolean(state?.normalizedToClosest);

            if (!form || !picker || !product || !activeVariant) return;

            if (syncForm && normalizedToClosest) {
                syncFormOptionsToVariant(form, activeVariant);
            }

            updateFormVariantId(form, activeVariant);

            if (activeVariant?.id) {
                picker.dataset.kiksSelectedVariantId = String(activeVariant.id);
                syncVariantIdToUrl(activeVariant.id, picker);
            }

            this._updateTargetCards(form, product, activeVariant, activeSelections);
            this._updateSizeCards(form, product, activeVariant);
            this._updateOptionValueSelectability(form, picker, product, activeSelections);
            this._updateSizeDropdownItems(picker, form, product, activeSelections);
            this._updateSizeDropdownToggle(picker, activeVariant);
            this._updateCardGroups(form, picker, exactVariant || activeVariant);

            const priceWrapper = getPriceWrapperFromPicker(picker);
            updatePriceWrapper(priceWrapper, activeVariant);
        }

        _getVariantPickerForForm(form) {
            if (!form) return null;
            const VARIANT_PICKER_SELECTOR = 'variant-picker';
            const closestPicker = form.closest(VARIANT_PICKER_SELECTOR);
            if (closestPicker) return closestPicker;

            if (form.id) {
                const escapedFormId = escapeSelector(form.id);
                const pickerByFormAttr = document.querySelector(`${VARIANT_PICKER_SELECTOR}[form="${escapedFormId}"]`);
                if (pickerByFormAttr) return pickerByFormAttr;

                const inputWithFormAttr = document.querySelector(`input[form="${escapedFormId}"]`);
                if (inputWithFormAttr) {
                    const pickerFromInput = inputWithFormAttr.closest(VARIANT_PICKER_SELECTOR);
                    if (pickerFromInput) return pickerFromInput;
                }
            }
            return null;
        }

        _getVariantPickerFromInput(input) {
            if (!input) return null;
            const VARIANT_PICKER_SELECTOR = 'variant-picker';
            const closestPicker = input.closest(VARIANT_PICKER_SELECTOR);
            if (closestPicker) return closestPicker;

            const formId = input.getAttribute('form');
            if (formId) {
                const escapedFormId = escapeSelector(formId);
                return document.querySelector(`${VARIANT_PICKER_SELECTOR}[form="${escapedFormId}"]`);
            }
            return null;
        }

        async refreshPricing(form, input) {
            if (!form) return;
            let picker = this._getVariantPickerForForm(form);
            if (!picker && input) {
                picker = this._getVariantPickerFromInput(input);
            }
            if (!picker) return;

            const nextRequestId = (formRefreshRequestIds.get(form) || 0) + 1;
            formRefreshRequestIds.set(form, nextRequestId);

            // Apply an immediate local update so UI reflects the click without waiting for async product JSON.
            this._applyOptimisticPickerState(form, picker, input);

            let refreshPromise = null;
            refreshPromise = (async () => {
                const handle = picker.getAttribute('handle') || picker.dataset.handle;
                if (!handle) return;

                const product = await loadProductData(handle);
                if (!product) return;

                if (formRefreshRequestIds.get(form) !== nextRequestId) return;

                const selections = getSelectedOptionValuesForPicker(picker);
                const state = resolvePricingState(product, selections, input);
                if (formRefreshRequestIds.get(form) !== nextRequestId) return;
                this._applyPricingState(form, picker, state, { syncForm: true });
            })()
                .catch((error) => {
                    console.error('[kiks] Failed to refresh pricing', error);
                })
                .finally(() => {
                    if (formRefreshPromises.get(form) === refreshPromise) {
                        formRefreshPromises.delete(form);
                    }
                });

            formRefreshPromises.set(form, refreshPromise);
            return refreshPromise;
        }

        _updateTargetCards(form, product, baseVariant, selections = null) {
            const normalizedProduct = normalizeProductVariants(product);
            if (!form || !normalizedProduct || !baseVariant) return;
            const basePrice = normalizeMoneyValue(baseVariant.price);
            const resolvedSelections = Array.isArray(selections) ? selections.slice() : null;
            if (resolvedSelections) {
                const optionCount = normalizedProduct.options?.length || 0;
                while (resolvedSelections.length < optionCount) {
                    resolvedSelections.push(null);
                }
            }

            const picker = this._getVariantPickerForForm(form);
            const cards = picker
                ? picker.querySelectorAll('[data-kiks-target-card]')
                : document.querySelectorAll('[data-kiks-target-card]');

            cards.forEach((card) => {
                const cardElement = card instanceof KiksTargetCard ? card : null;
                if (!cardElement) return;

                const input = cardElement._input || (typeof cardElement._getInput === 'function' ? cardElement._getInput() : null);
                if (input && !cardElement._input) {
                    cardElement._input = input;
                }

                let cardVariant = null;
                const optionIndex = cardElement.getOptionIndex();
                const optionValue = cardElement.getOptionValue();
                if (optionIndex != null && optionValue != null && optionValue !== '') {
                    if (resolvedSelections) {
                        cardVariant = resolveVariantForOptionValueFromOptions(normalizedProduct, resolvedSelections, optionIndex, optionValue);
                    }
                }

                if (!cardVariant) {
                    cardElement.dataset.targetPrice = '';
                    clearTargetCardDelta(card.querySelector('.variant-target-card__delta'));
                    cardElement.updateAvailability(false, false);
                    return;
                }

                const cardPrice = normalizeMoneyValue(cardVariant.price);
                cardElement.dataset.targetPrice = cardPrice != null ? String(cardPrice) : '';

                if (basePrice != null) {
                    cardElement.updatePricing(cardVariant, basePrice);
                } else {
                    clearTargetCardDelta(card.querySelector('.variant-target-card__delta'));
                }
                // Existing combinations are selectable; availability only controls Sold Out/add-to-cart state.
                cardElement.updateAvailability(Boolean(cardVariant.available), true);
            });
        }

        _updateSizeCards(form, product, baseVariant) {
            const normalizedProduct = normalizeProductVariants(product);
            if (!form || !normalizedProduct || !baseVariant) return;

            const picker = this._getVariantPickerForForm(form);
            const cards = picker
                ? picker.querySelectorAll('[data-kiks-size-card]')
                : document.querySelectorAll('[data-kiks-size-card]');

            cards.forEach((card) => {
                const cardElement = card instanceof KiksSizeCard ? card : null;
                if (!cardElement) return;

                const input = cardElement._input || (typeof cardElement._getInput === 'function' ? cardElement._getInput() : null);
                if (input && !cardElement._input) {
                    cardElement._input = input;
                }

                let cardVariant = null;
                const optionIndex = cardElement.getOptionIndex();
                if (optionIndex != null) {
                    const optionValue = cardElement.getOptionValue();
                    cardVariant = resolveVariantForOptionValue(normalizedProduct, baseVariant, optionIndex, optionValue);
                }

                if (!cardVariant && card.dataset.sizeVariantId) {
                    cardVariant = getVariantById(normalizedProduct, card.dataset.sizeVariantId);
                }

                if (cardVariant) {
                    cardElement.updatePricing(cardVariant);
                }
            });
        }

        _updateSizeDropdownToggle(picker, baseVariant) {
            if (!picker || !baseVariant) return;

            const priceValue = normalizeMoneyValue(baseVariant.price);
            const compareAtValue = normalizeMoneyValue(baseVariant.compare_at_price);
            const refPrice = Number.isFinite(compareAtValue) && Number.isFinite(priceValue) && compareAtValue > priceValue
                ? compareAtValue : priceValue;

            picker.querySelectorAll('.variant-picker__option--size-select [data-dropdown-variant-picker-label]').forEach((toggleLabel) => {
                const currentPriceEl = toggleLabel.querySelector('[data-kiks-current-price]');
                if (currentPriceEl && priceValue != null) {
                    currentPriceEl.textContent = formatMoney(priceValue);
                    currentPriceEl.dataset.kiksCurrentPrice = String(baseVariant.price);
                }

                const comparePriceEl = toggleLabel.querySelector('[data-kiks-compare-price]');
                if (comparePriceEl && refPrice != null) {
                    comparePriceEl.textContent = `(Total Value ${formatMoney(refPrice)})`;
                    comparePriceEl.dataset.kiksComparePrice = String(refPrice);
                }
            });
        }

        _getSizeDropdownItemsForPicker(picker, form) {
            if (!picker) return [];
            const items = new Set();
            picker.querySelectorAll('.variant-picker-dropdown__item--size[for]').forEach((item) => items.add(item));

            if (picker.id) {
                const escapedPickerId = escapeSelector(picker.id);
                document.querySelectorAll(`.variant-picker-dropdown__item--size[for][data-kiks-picker-id="${escapedPickerId}"]`)
                    .forEach((item) => items.add(item));
            }

            const formId = form?.id || picker.getAttribute('form');
            if (formId) {
                const escapedFormId = escapeSelector(formId);
                document.querySelectorAll(`.variant-picker-dropdown__item--size[for][data-kiks-form-id="${escapedFormId}"]`)
                    .forEach((item) => items.add(item));
            }

            return Array.from(items);
        }

        _updateSizeDropdownItems(picker, form, product, selections) {
            const normalizedProduct = normalizeProductVariants(product);
            if (!picker || !normalizedProduct) return;

            const resolvedSelections = normalizeSelectionsForProduct(normalizedProduct, selections);
            const items = this._getSizeDropdownItemsForPicker(picker, form);

            items.forEach((item) => {
                const optionPosition = Number(item.dataset.kiksOptionPosition);
                const optionIndex = Number.isFinite(optionPosition) && optionPosition > 0
                    ? optionPosition - 1
                    : null;
                const optionValue = item.dataset.kiksOptionValue;

                let sizeVariant = null;
                if (optionIndex != null && optionValue != null && optionValue !== '') {
                    sizeVariant = resolveVariantForOptionValueFromOptions(
                        normalizedProduct,
                        resolvedSelections,
                        optionIndex,
                        optionValue
                    );
                }

                const priceEl = item.querySelector('.variant-size-card__price');
                const totalValueEl = item.querySelector('.variant-size-card__total-value');

                if (!sizeVariant) {
                    if (priceEl) {
                        priceEl.textContent = '';
                        priceEl.dataset.kiksCurrentPrice = '';
                    }
                    if (totalValueEl) {
                        totalValueEl.textContent = '';
                        totalValueEl.dataset.kiksComparePrice = '';
                    }
                    item.classList.add('variant-size-card--unavailable');
                    item.setAttribute('data-no-available', '');
                    item.setAttribute('aria-disabled', 'true');
                    item.tabIndex = -1;
                    return;
                }

                const isAvailable = Boolean(sizeVariant.available);
                item.classList.toggle('variant-size-card--unavailable', !isAvailable);
                item.setAttribute('aria-disabled', isAvailable ? 'false' : 'true');
                item.tabIndex = isAvailable ? 0 : -1;
                if (isAvailable) {
                    item.removeAttribute('data-no-available');
                } else {
                    item.setAttribute('data-no-available', '');
                }

                const priceValue = normalizeMoneyValue(sizeVariant.price);
                const compareAtValue = normalizeMoneyValue(sizeVariant.compare_at_price);
                const refPrice = Number.isFinite(compareAtValue) && Number.isFinite(priceValue) && compareAtValue > priceValue
                    ? compareAtValue : priceValue;

                if (priceEl && priceValue != null) {
                    priceEl.textContent = formatMoney(priceValue);
                    priceEl.dataset.kiksCurrentPrice = String(sizeVariant.price);
                }

                if (totalValueEl && refPrice != null) {
                    totalValueEl.textContent = `(Total Value ${formatMoney(refPrice)})`;
                    totalValueEl.dataset.kiksComparePrice = String(refPrice);
                }
            });
        }

        _syncSizeDropdownItemStates(picker, form) {
            if (!picker) return;
            const items = this._getSizeDropdownItemsForPicker(picker, form);

            items.forEach((item) => {
                const input = getInputByOptionPositionAndValue(
                    picker,
                    item.dataset.kiksOptionPosition,
                    item.dataset.kiksOptionValue
                ) || getInputByIdInContext(item.getAttribute('for'), picker);

                const isSelectable = Boolean(input) && !input.disabled;

                if (!isSelectable) {
                    // Combination doesn't exist — always mark unavailable
                    item.classList.add('variant-size-card--unavailable');
                    item.setAttribute('aria-disabled', 'true');
                    item.tabIndex = -1;
                    item.setAttribute('data-no-available', '');
                } else {
                    // Variant exists — preserve availability state already set by Liquid
                    // or by _updateSizeDropdownItems (which uses variant.available).
                    // Only sync tabIndex/aria-disabled to match the current data-no-available value.
                    const isUnavailable = item.hasAttribute('data-no-available');
                    item.classList.toggle('variant-size-card--unavailable', isUnavailable);
                    item.setAttribute('aria-disabled', isUnavailable ? 'true' : 'false');
                    item.tabIndex = isUnavailable ? -1 : 0;
                }
            });
        }

        _getOptionControlsForInput(picker, form, inputId) {
            if (!inputId) return [];
            const controls = new Set();
            const escapedInputId = escapeSelector(inputId);

            picker.querySelectorAll(`[for="${escapedInputId}"]`).forEach((el) => controls.add(el));

            if (picker.id) {
                const escapedPickerId = escapeSelector(picker.id);
                document.querySelectorAll(`[for="${escapedInputId}"][data-kiks-picker-id="${escapedPickerId}"]`)
                    .forEach((el) => controls.add(el));
            }

            const formId = form?.id || picker.getAttribute('form');
            if (formId) {
                const escapedFormId = escapeSelector(formId);
                document.querySelectorAll(`[for="${escapedInputId}"][data-kiks-form-id="${escapedFormId}"]`)
                    .forEach((el) => controls.add(el));
            }

            return Array.from(controls);
        }

        _syncInputSelectableUi(picker, form, input, isSelectable) {
            if (!picker || !input?.id) return;
            const controls = this._getOptionControlsForInput(picker, form, input.id);

            controls.forEach((control) => {
                const isSizeCardHost = control.matches && control.matches('kiks-size-card');
                if (isSizeCardHost || control.hasAttribute('data-kiks-size-card')) {
                    control.classList.toggle('variant-size-card--unavailable', !isSelectable);
                    if (isSelectable) {
                        control.removeAttribute('data-unavailable');
                        control.removeAttribute('data-no-available');
                    } else {
                        control.setAttribute('data-unavailable', '');
                        control.setAttribute('data-no-available', '');
                    }
                    const innerSizeCard = control.querySelector('.variant-size-card');
                    if (innerSizeCard) {
                        innerSizeCard.classList.toggle('variant-size-card--unavailable', !isSelectable);
                    }
                }

                if (control.classList && control.classList.contains('variant-picker-dropdown__item--size')) {
                    control.classList.toggle('variant-size-card--unavailable', !isSelectable);
                    control.setAttribute('aria-disabled', isSelectable ? 'false' : 'true');
                    control.tabIndex = isSelectable ? 0 : -1;
                    if (isSelectable) {
                        control.removeAttribute('data-no-available');
                    } else {
                        control.setAttribute('data-no-available', '');
                    }
                }

                if (control.classList && control.classList.contains('kik-variant-button-pill')) {
                    control.classList.toggle('kik-variant-button-pill--unavailable', !isSelectable);
                    control.setAttribute('aria-disabled', isSelectable ? 'false' : 'true');
                }
            });
        }

        _updateOptionValueSelectability(form, picker, product, selections) {
            const normalizedProduct = normalizeProductVariants(product);
            if (!form || !picker || !normalizedProduct) return;

            const resolvedSelections = normalizeSelectionsForProduct(normalizedProduct, selections);
            const optionGroups = picker.querySelectorAll('[data-variant-picker-option-inputs]');

            optionGroups.forEach((group, groupIndex) => {
                group.querySelectorAll('input[type="radio"]').forEach((input) => {
                    const inputOptionIndex = getOptionIndexFromInputName(input.name);
                    const optionIndex = inputOptionIndex != null ? inputOptionIndex : groupIndex;
                    const matchingVariant = resolveVariantForOptionValueFromOptions(
                        normalizedProduct,
                        resolvedSelections,
                        optionIndex,
                        input.value
                    );
                    const isSelectable = Boolean(matchingVariant);
                    input.disabled = !isSelectable;
                    this._syncInputSelectableUi(picker, form, input, isSelectable);
                });
            });

            this._syncSizeDropdownItemStates(picker, form);
        }

        _updateCardGroups(container, picker = null, baseVariant = null) {
            // Get the current variant price to use as base for delta calculations
            let basePrice = null;
            if (baseVariant) {
                basePrice = normalizeMoneyValue(baseVariant.price);
                if (basePrice == null) basePrice = null;
            }

            const scope = picker || this._getVariantPickerForForm(container) || container || document;
            const processedTargetNames = new Set();
            scope.querySelectorAll('[data-kiks-target-card]').forEach((card) => {
                const cardElement = card instanceof KiksTargetCard ? card : null;
                if (!cardElement) return;
                const input = cardElement._input || (typeof cardElement._getInput === 'function' ? cardElement._getInput() : null);
                if (input && !cardElement._input) {
                    cardElement._input = input;
                }
                const groupName = input?.name || cardElement.dataset.targetName;
                if (!groupName) return;
                if (processedTargetNames.has(groupName)) return;
                processedTargetNames.add(groupName);
                cardElement._updateGroup(basePrice);
            });

            const processedSizeNames = new Set();
            scope.querySelectorAll('[data-kiks-size-card]').forEach((card) => {
                const cardElement = card instanceof KiksSizeCard ? card : null;
                if (!cardElement) return;
                const input = cardElement._input || (typeof cardElement._getInput === 'function' ? cardElement._getInput() : null);
                if (input && !cardElement._input) {
                    cardElement._input = input;
                }
                const groupName = input?.name || cardElement.dataset.sizeName;
                if (!groupName) return;
                if (processedSizeNames.has(groupName)) return;
                processedSizeNames.add(groupName);
                cardElement._updateSelectedState();
            });
        }

        _refreshAll() {
            const runRefresh = () => {
                const pickers = document.querySelectorAll('variant-picker');
                pickers.forEach((picker) => {
                    const formId = picker.getAttribute('form');
                    let form = null;
                    if (formId) {
                        form = document.forms[formId] || document.getElementById(formId);
                    }
                    if (!form) {
                        form = picker.querySelector('form');
                    }
                    if (form) {
                        const inputInPicker = picker.querySelector('input[form], input[name^="option"]');
                        this.refreshPricing(form, inputInPicker);
                        this._setupPillObserver();
                    }
                });
            };
            // Run immediately and after a short delay so size/target cards and form are ready
            runRefresh();
            setTimeout(runRefresh, 100);
        }
    }

    // ============================================================================
    // Register Components
    // ============================================================================

    if (!window.customElements.get('kiks-sale-timer')) {
        window.customElements.define('kiks-sale-timer', KiksSaleTimer);
    }

    if (!window.customElements.get('kiks-size-card')) {
        window.customElements.define('kiks-size-card', KiksSizeCard);
    }

    if (!window.customElements.get('kiks-target-card')) {
        window.customElements.define('kiks-target-card', KiksTargetCard);
    }

    if (!window.customElements.get('kiks-product-pricing')) {
        window.customElements.define('kiks-product-pricing', KiksProductPricing);
    }

    // ============================================================================
    // Backward Compatibility & Initialization
    // ============================================================================

    // Enhance existing elements with component behavior
    function enhanceElements(root = document) {
        // Enhance sale timers (replace with custom element for better lifecycle)
        root.querySelectorAll('[data-kiks-sale-timer]').forEach((el) => {
            if (!(el instanceof KiksSaleTimer) && !el._kiksEnhanced) {
                const timer = document.createElement('kiks-sale-timer');
                Array.from(el.attributes).forEach((attr) => {
                    timer.setAttribute(attr.name, attr.value);
                });
                timer.innerHTML = el.innerHTML;
                el.parentNode?.replaceChild(timer, el);
            }
        });

        // Enhance tabs (replace with custom element)
        root.querySelectorAll('[data-kiks-tabs]').forEach((el) => {
            if (!(el instanceof KiksTabs) && !el._kiksEnhanced) {
                el._kiksEnhanced = true; // Mark as enhanced to prevent re-processing
                const tabs = document.createElement('kiks-tabs');
                Array.from(el.attributes).forEach((attr) => {
                    tabs.setAttribute(attr.name, attr.value);
                });
                tabs.innerHTML = el.innerHTML;
                const parent = el.parentNode;
                if (parent) {
                    parent.replaceChild(tabs, el);
                    // Ensure initialization - connectedCallback should fire, but add fallback
                    // Use setTimeout to ensure it runs after connectedCallback
                    setTimeout(() => {
                        if (tabs.isConnected && tabs.dataset.kiksTabsInitialized !== 'true') {
                            // Force initialization if connectedCallback didn't complete
                            const triggers = tabs.querySelectorAll('[data-tab-target]');
                            const panels = tabs.querySelectorAll('[data-tab-panel]');
                            if (triggers.length > 0 && panels.length > 0) {
                                tabs.dataset.kiksTabsInitialized = 'true';
                                tabs._init();
                            }
                        }
                    }, 0);
                }
            }
        });

        // Enhance size cards (attach behavior without replacing)
        root.querySelectorAll('[data-kiks-size-card]').forEach((el) => {
            if (el._kiksComponent || el instanceof KiksSizeCard) return;

            // Create component instance and attach to element
            const component = document.createElement('kiks-size-card');
            // Copy element's properties to component
            Array.from(el.attributes).forEach((attr) => {
                component.setAttribute(attr.name, attr.value);
            });
            component.innerHTML = el.innerHTML;

            // Replace element with component
            el.parentNode?.replaceChild(component, el);
        });

        // Enhance target cards (attach behavior without replacing)
        root.querySelectorAll('[data-kiks-target-card]').forEach((el) => {
            if (el._kiksComponent || el instanceof KiksTargetCard) return;

            // Create component instance and attach to element
            const component = document.createElement('kiks-target-card');
            // Copy element's properties to component
            Array.from(el.attributes).forEach((attr) => {
                component.setAttribute(attr.name, attr.value);
            });
            component.innerHTML = el.innerHTML;

            // Replace element with component
            el.parentNode?.replaceChild(component, el);
        });
    }

    // Initialize pricing manager
    function initPricingManager() {
        let pricingManager = document.querySelector('kiks-product-pricing');
        if (!pricingManager) {
            pricingManager = document.createElement('kiks-product-pricing');
            document.body.appendChild(pricingManager);
        }
    }

    // Initialize on DOM ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            enhanceElements();
            initPricingManager();
        });
    } else {
        enhanceElements();
        initPricingManager();
    }

    // Handle Shopify section events
    document.addEventListener('shopify:section:load', (event) => {
        if (event.target) {
            enhanceElements(event.target);
            initChatAppTriggers(event.target);
        }
    });

    document.addEventListener('shopify:section:select', (event) => {
        if (event.target) {
            enhanceElements(event.target);
        }
    });

    document.addEventListener('shopify:block:select', (event) => {
        if (event.target) {
            enhanceElements(event.target);

            // Handle tab activation
            const tabContainer = event.target.closest('[data-kiks-tabs]') ||
                event.target.querySelector('[data-kiks-tabs]');
            if (tabContainer && tabContainer instanceof KiksTabs) {
                const panel = event.target.closest('[data-tab-panel]');
                if (panel) {
                    tabContainer.activateTab(panel.dataset.tabPanel);
                } else if (event.target.matches('[data-tab-target]')) {
                    tabContainer.activateTab(event.target.dataset.tabTarget);
                }
            }
        }
    });

    document.addEventListener('shopify:section:unload', (event) => {
        // Components will handle cleanup via disconnectedCallback
    });

    // Product tabs: keep tabs clickable after variant DOM swaps by using delegated events.
    function setActiveProductTab(root, tabIndex) {
        if (!root) return;
        const labels = root.querySelectorAll('.product-tabs__name');
        const panels = root.querySelectorAll('.product-tabs__panel');
        const inputs = root.querySelectorAll('.product-tabs__input');
        if (labels.length === 0 || panels.length === 0 || inputs.length === 0) return;

        const maxIndex = Math.min(labels.length, panels.length, inputs.length);
        const index = Math.max(1, Math.min(tabIndex, maxIndex));

        panels.forEach((panel, i) => {
            panel.style.display = i + 1 === index ? 'block' : 'none';
        });

        labels.forEach((label, i) => {
            label.classList.toggle('selected', i + 1 === index);
        });

        inputs.forEach((input, i) => {
            input.checked = i + 1 === index;
        });
    }

    function syncProductTabsFromChecked(root) {
        if (!root) return;
        const checked = root.querySelector('.product-tabs__input:checked');
        const fallback = root.querySelector('.product-tabs__input');
        const source = checked || fallback;
        if (!source) return;
        const parsedIndex = Number.parseInt(source.dataset.tabIndex || '', 10);
        const tabIndex = Number.isFinite(parsedIndex) && parsedIndex > 0 ? parsedIndex : 1;
        setActiveProductTab(root, tabIndex);
    }

    function initProductTabs(scope = document) {
        const root = scope && typeof scope.querySelectorAll === 'function' ? scope : document;
        root.querySelectorAll('[data-product-tabs]').forEach((tabsRoot) => {
            syncProductTabsFromChecked(tabsRoot);
        });
    }

    if (!window.__kiksProductTabsDelegatedBound) {
        const onProductTabActivate = (event) => {
            const label = event.target && event.target.closest
                ? event.target.closest('.product-tabs__name')
                : null;
            if (!label) return;

            const root = label.closest('[data-product-tabs]');
            if (!root) return;

            if (event.type === 'keydown') {
                if (event.key !== 'Enter' && event.key !== ' ') return;
                event.preventDefault();
            }

            const inputId = label.getAttribute('for');
            const input = inputId ? root.querySelector('#' + inputId) : null;
            if (input) {
                input.checked = true;
                const parsedIndex = Number.parseInt(input.dataset.tabIndex || '', 10);
                const tabIndex = Number.isFinite(parsedIndex) && parsedIndex > 0
                    ? parsedIndex
                    : 1;
                setActiveProductTab(root, tabIndex);
                return;
            }

            const labels = Array.from(root.querySelectorAll('.product-tabs__name'));
            const fallbackIndex = labels.indexOf(label) + 1;
            if (fallbackIndex > 0) {
                setActiveProductTab(root, fallbackIndex);
            }
        };

        const onProductTabInputChange = (event) => {
            const target = event.target;
            if (!(target instanceof HTMLInputElement)) return;
            if (!target.matches('.product-tabs__input')) return;

            const root = target.closest('[data-product-tabs]');
            if (!root) return;

            const parsedIndex = Number.parseInt(target.dataset.tabIndex || '', 10);
            const tabIndex = Number.isFinite(parsedIndex) && parsedIndex > 0 ? parsedIndex : 1;
            setActiveProductTab(root, tabIndex);
        };

        document.addEventListener('click', onProductTabActivate);
        document.addEventListener('keydown', onProductTabActivate);
        document.addEventListener('change', onProductTabInputChange);
        window.__kiksProductTabsDelegatedBound = true;
    }

    initProductTabs();
    // Carry current tab index via section render URL so server-rendered HTML already
    // returns the correct selected tab/panel after variant swap.
    function getCurrentProductTabIndexForPicker(picker) {
        const scope = picker?.closest?.('.shopify-section') || document;
        const tabsRoot = scope.querySelector('[data-product-tabs]');
        if (!tabsRoot) return null;

        const checked = tabsRoot.querySelector('.product-tabs__input:checked');
        if (checked) {
            const parsed = Number.parseInt(checked.dataset.tabIndex || '', 10);
            if (Number.isFinite(parsed) && parsed > 0) return parsed;
        }

        const selectedLabel = tabsRoot.querySelector('.product-tabs__name.selected');
        if (selectedLabel) {
            const labels = Array.from(tabsRoot.querySelectorAll('.product-tabs__name'));
            const index = labels.indexOf(selectedLabel) + 1;
            if (index > 0) return index;
        }

        return 1;
    }

    function appendTabIndexToVariantFetchUrl(urlValue, picker) {
        if (!urlValue) return urlValue;
        const activeTabIndex = getCurrentProductTabIndexForPicker(picker);
        if (!activeTabIndex) return urlValue;

        try {
            const parsed = new URL(urlValue, window.location.origin);
            parsed.searchParams.set('kik_tab_index', String(activeTabIndex));
            return parsed.toString();
        } catch (error) {
            return urlValue;
        }
    }

    function patchVariantPickerFetchUrlWithTabState() {
        if (window.__kiksVariantPickerTabFetchPatched) return;
        const VariantPickerClass = window.customElements?.get?.('variant-picker');
        if (!VariantPickerClass || !VariantPickerClass.prototype) return;
        const originalGetFetchUrl = VariantPickerClass.prototype.getFetchUrl;
        if (typeof originalGetFetchUrl !== 'function') return;

        VariantPickerClass.prototype.getFetchUrl = function(clickedInputEl) {
            const requestUrl = originalGetFetchUrl.call(this, clickedInputEl);
            return appendTabIndexToVariantFetchUrl(requestUrl, this);
        };

        window.__kiksVariantPickerTabFetchPatched = true;
    }

    function captureProductTabsSelection(scope) {
        const selection = {};
        if (!scope || typeof scope.querySelectorAll !== 'function') return selection;

        scope.querySelectorAll('[data-product-tabs]').forEach((tabsRoot) => {
            if (!tabsRoot.id) return;
            const checked = tabsRoot.querySelector('.product-tabs__input:checked');
            if (checked) {
                const parsed = Number.parseInt(checked.dataset.tabIndex || '', 10);
                if (Number.isFinite(parsed) && parsed > 0) {
                    selection[tabsRoot.id] = parsed;
                    return;
                }
            }

            const selectedLabel = tabsRoot.querySelector('.product-tabs__name.selected');
            if (!selectedLabel) return;
            const labels = Array.from(tabsRoot.querySelectorAll('.product-tabs__name'));
            const index = labels.indexOf(selectedLabel) + 1;
            if (index > 0) {
                selection[tabsRoot.id] = index;
            }
        });

        return selection;
    }

    function applyProductTabsSelection(scope, selection) {
        if (!scope || !selection || typeof scope.querySelectorAll !== 'function') return;
        scope.querySelectorAll('[data-product-tabs]').forEach((tabsRoot) => {
            if (!tabsRoot.id) return;
            const index = selection[tabsRoot.id];
            if (!index) return;
            setActiveProductTab(tabsRoot, index);
        });
    }

    function patchVariantPickerSwapWithTabState() {
        if (window.__kiksVariantPickerTabSwapPatched) return;
        const VariantPickerClass = window.customElements?.get?.('variant-picker');
        if (!VariantPickerClass || !VariantPickerClass.prototype) return;
        const originalSwapProduct = VariantPickerClass.prototype.swapProduct;
        const originalUpdateElements = VariantPickerClass.prototype.updateElements;
        if (typeof originalSwapProduct !== 'function' || typeof originalUpdateElements !== 'function') return;

        VariantPickerClass.prototype.swapProduct = function(url) {
            const scope = this.closest?.('.shopify-section') || document;
            this.__kiksTabsSelectionBeforeSwap = captureProductTabsSelection(scope);
            return originalSwapProduct.call(this, url);
        };

        VariantPickerClass.prototype.updateElements = function(html, shouldSwapProduct) {
            let result;
            try {
                result = originalUpdateElements.call(this, html, shouldSwapProduct);
            } catch (err) {
                // Theme's updateElements expects .product-variant-picker__list; in cart-drawer or
                // other contexts that element may be missing, causing querySelectorAll on null.
                if (err && typeof err.message === 'string' && err.message.includes('querySelectorAll')) {
                    if (typeof console !== 'undefined' && console.warn) {
                        console.warn('[kik-product] variant-picker updateElements skipped: required DOM not present', err.message);
                    }
                    return undefined;
                }
                throw err;
            }
            const selection = this.__kiksTabsSelectionBeforeSwap;
            if (selection) {
                const scope = this.closest?.('.shopify-section') || document;
                applyProductTabsSelection(scope, selection);
                setTimeout(() => applyProductTabsSelection(scope, selection), 0);
                setTimeout(() => applyProductTabsSelection(scope, selection), 80);
            }
            return result;
        };

        window.__kiksVariantPickerTabSwapPatched = true;
    }

    patchVariantPickerFetchUrlWithTabState();
    patchVariantPickerSwapWithTabState();
    if (window.customElements?.whenDefined) {
        window.customElements.whenDefined('variant-picker').then(() => {
            patchVariantPickerFetchUrlWithTabState();
            patchVariantPickerSwapWithTabState();
        });
    }

    // Variant Modal
    // Function to get launcher iframe (in case it's loaded dynamically)
    function getLauncherIframe() {
        return document.querySelector('iframe#launcher');
    }

    // Function to hide/show launcher iframe
    function toggleLauncherIframe(hide) {
        const launcherIframe = getLauncherIframe();
        if (launcherIframe) {
            if (hide) {
                launcherIframe.classList.add('hidden');
                launcherIframe.style.display = 'none';
            } else {
                launcherIframe.classList.remove('hidden');
                launcherIframe.style.display = '';
            }
        }
    }

    function getHeaderForModal() {
        return document.querySelector('header.shopify-section-header');
    }

    function setHeaderZIndexForModal(enable) {
        var header = getHeaderForModal();
        if (!header) return;
        if (enable) {
            header.style.setProperty('z-index', '1');
        } else {
            header.style.removeProperty('z-index');
        }
    }

    function setBodyOverlayBelowModal(enable) {
        var overlays = document.querySelectorAll('.body-overlay, [class*="body-overlay"], body-element.body-overlay');
        overlays.forEach(function (el) {
            if (enable) {
                el.style.zIndex = '1';
            } else {
                el.style.removeProperty('z-index');
            }
        });
    }

    // Variant info modal now uses kik_size_guide popup (same as size guide); opened via .kik_guide click handler below

    // Initialize when DOM is ready
    function initModalLauncherToggle() {
        // Handle overlay clicks to close modal (for any remaining drawer-style modals)
        document.querySelectorAll('.kiks-variant-modal').forEach(function (modal) {
            modal.addEventListener('click', function (event) {
                // Close if clicking the overlay (not the content)
                if (event.target === modal) {
                    if (typeof modal.hide === 'function') {
                        modal.hide();
                    } else if (typeof modal.close === 'function') {
                        modal.close();
                    } else {
                        modal.removeAttribute('open');
                    }
                    document.body.classList.remove('kiks-variant-modal-active');
                    setHeaderZIndexForModal(false);
                    setBodyOverlayBelowModal(false);
                }
            });

            // Hide launcher iframe when modal opens
            modal.addEventListener('dialog:after-show', function () {
                toggleLauncherIframe(true);
            });

            // Show launcher iframe when modal closes; restore header and body-overlay
            modal.addEventListener('dialog:after-hide', function () {
                toggleLauncherIframe(false);
                document.body.classList.remove('kiks-variant-modal-active');
                setHeaderZIndexForModal(false);
                setBodyOverlayBelowModal(false);
            });
        });
    }

    // Run when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initModalLauncherToggle);
    } else {
        initModalLauncherToggle();
    }

    // Also watch for dynamically added modals
    const observer = new MutationObserver(function (mutations) {
        const modals = document.querySelectorAll('.kiks-variant-modal');
        if (modals.length > 0) {
            modals.forEach(function (modal) {
                if (!modal.dataset.launcherToggleInitialized) {
                    modal.dataset.launcherToggleInitialized = 'true';
                    modal.addEventListener('dialog:after-show', function () {
                        toggleLauncherIframe(true);
                    });
                    modal.addEventListener('dialog:after-hide', function () {
                        toggleLauncherIframe(false);
                    });
                }
            });
        }
    });

    observer.observe(document.body, {
        childList: true,
        subtree: true
    });



    // Size guide and variant modal popups: open via .kik_guide (data-popup), close via overlay or .size_guide_close
    document.addEventListener('click', function (event) {
        // Close kik_size_guide when footer button with href="#" is clicked
        const footerButton = event.target.closest && event.target.closest('.size_guide_footer .kiks-variant-modal__button');
        if (footerButton && (footerButton.getAttribute('href') === '#' || (footerButton.getAttribute('href') || '').trim() === '#')) {
            event.preventDefault();
            const guide = footerButton.closest('.kik_size_guide');
            if (guide) {
                if (guide.getAttribute("data-popup") === "thickness") {
                    closeThicknessGuideDrawer();
                } else {
                    guide.classList.remove('active');
                }
            }
            return;
        }

        const trigger = event.target.closest && event.target.closest('.kik_guide');
        if (trigger) {
            const popupValue = trigger.getAttribute('data-popup');
            if (popupValue) {
                if (popupValue === "thickness") {
                    openThicknessGuideDrawer();
                }
                else {
                    const target = document.querySelector('.kik_size_guide[data-popup="' + popupValue + '"]');
                    if (target) {
                        target.classList.toggle('active');
                    }
                }
            }
            return;
        }
        const closeTrigger = event.target.closest && event.target.closest('.kik_size_overlay, .size_guide_close');
        if (closeTrigger) {
            const guide = closeTrigger.closest && closeTrigger.closest('.kik_size_guide');
            if (guide) {
                if (guide.getAttribute("data-popup") === "thickness") {
                    closeThicknessGuideDrawer();
                }
                else {
                    guide.classList.remove('active');
                }
            }
        }
    });



    // Thickness Guide Drawer (same modal pattern as kik_size_guide)

    function closeThicknessGuideDrawer() {
        const guide = document.querySelector(".kik_size_guide[data-popup=\"thickness\"]");
        if (guide) {
            guide.classList.remove("active");
        }
        document.body.style.overflow = "";
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.width = "";
        document.documentElement.style.overflow = "";
        document.body.classList.remove("thickness-guide-open");
        document.documentElement.classList.remove("thickness-guide-open");
        const scrollPosition = parseInt(document.body.style.top || "0", 10) * -1;
        if (!isNaN(scrollPosition)) {
            window.scrollTo(0, scrollPosition);
        }
    }

    function openThicknessGuideDrawer() {
        const guide = document.querySelector(".kik_size_guide[data-popup=\"thickness\"]");
        if (guide) {
            const scrollPosition =
                window.pageYOffset || document.documentElement.scrollTop;
            document.body.style.overflow = "hidden";
            document.body.style.position = "fixed";
            document.body.style.top = "-" + scrollPosition + "px";
            document.body.style.width = "100%";
            document.documentElement.style.overflow = "hidden";
            document.body.classList.add("thickness-guide-open");
            document.documentElement.classList.add("thickness-guide-open");
            guide.classList.add("active");
            if (typeof guide._thicknessUpdateTotal === "function") {
                guide._thicknessUpdateTotal();
            } else {
                initThicknessGuideCalculator();
            }
        }
    }

    // Function to attach click handlers to Thickness Guide links
    function attachThicknessGuideClickHandlers() {
        const $ = window.jQuery || window.$;

        // Function to check if element is a thickness guide button
        function isThicknessGuideButton(btn) {
            const btnText = (btn.textContent || btn.innerText || "")
                .trim()
                .toLowerCase();
            return btnText.includes("thickness") || btnText.includes("guide");
        }

        // Close thickness guide when overlay or close button is clicked
        function bindThicknessGuideClose() {
            const overlay = document.getElementById("thicknessGuideDrawerOverlay");
            const closeBtn = document.getElementById("thicknessGuideDrawerClose");
            if (overlay && !overlay.dataset.thicknessCloseBound) {
                overlay.addEventListener("click", closeThicknessGuideDrawer);
                overlay.dataset.thicknessCloseBound = "true";
            }
            if (closeBtn && !closeBtn.dataset.thicknessCloseBound) {
                closeBtn.addEventListener("click", closeThicknessGuideDrawer);
                closeBtn.dataset.thicknessCloseBound = "true";
            }
        }
        if (document.readyState === "loading") {
            document.addEventListener("DOMContentLoaded", bindThicknessGuideClose);
        } else {
            bindThicknessGuideClose();
        }

        // Thickness guide calculator: total height = mattress + foundation + bed frame
        function initThicknessGuideCalculator() {
            const guide = document.querySelector(".kik_size_guide[data-popup=\"thickness\"]");
            if (!guide) return;

            const optionsEl = guide.querySelector("[data-thickness-options]");
            const singleSelect = optionsEl && optionsEl.getAttribute("data-thickness-single-select") === "true";
            const foundationSlider = guide.querySelector("#thicknessGuideFoundation");
            const foundationInput = guide.querySelector("#thicknessGuideFoundationInput");
            const bedFrameSlider = guide.querySelector("#thicknessGuideBedFrame");
            const bedFrameInput = guide.querySelector("#thicknessGuideBedFrameInput");
            const heightTotalSlider = guide.querySelector("#thicknessGuideHeightTotal");
            const heightFtInput = guide.querySelector("#thicknessGuideHeightFt");
            const heightInInput = guide.querySelector("#thicknessGuideHeightIn");
            const totalValueLeft = guide.querySelector("#thicknessGuideTotalValueLeft");
            const totalValueRight = guide.querySelector("#thicknessGuideTotalValueRight");
            const resultsRight = guide.querySelector("[data-thickness-results-right]");
            const stackMattressLeft = guide.querySelector("[data-stack-mattress-left]");
            const stackFoundationLeft = guide.querySelector("[data-stack-foundation-left]");
            const stackFrameLeft = guide.querySelector("[data-stack-frame-left]");
            const stackMattressRight = guide.querySelector("[data-stack-mattress-right]");
            const stackFoundationRight = guide.querySelector("[data-stack-foundation-right]");
            const stackFrameRight = guide.querySelector("[data-stack-frame-right]");

            function parseNum(val) {
                const n = parseFloat(val, 10);
                return isNaN(n) ? 0 : n;
            }

            let selectedThicknesses = [];

            function getSelectedThicknesses() {
                if (!optionsEl) return [12];
                const maxCount = singleSelect ? 1 : 2;
                if (selectedThicknesses.length) return selectedThicknesses.slice(0, maxCount);
                const activeInputs = optionsEl.querySelectorAll(".thickness-guide-option-input:checked");
                if (activeInputs.length) {
                    return Array.from(activeInputs)
                        .map(function (input) { return parseNum(input.getAttribute("data-thickness-inches") || input.value); })
                        .slice(0, maxCount);
                }
                const firstInput = optionsEl.querySelector(".thickness-guide-option-input");
                return firstInput ? [parseNum(firstInput.getAttribute("data-thickness-inches") || firstInput.value)] : [12];
            }

            function syncThicknessOptionStates() {
                if (!optionsEl) return;
                optionsEl.querySelectorAll(".thickness-guide-option").forEach(function (label) {
                    const input = label.querySelector(".thickness-guide-option-input");
                    if (!input) return;
                    const val = parseNum(input.getAttribute("data-thickness-inches") || input.value);
                    const shouldCheck = selectedThicknesses.indexOf(val) > -1;
                    input.checked = shouldCheck;
                    label.classList.toggle("is-checked", shouldCheck);
                });
            }

            function formatInches(value, decimals) {
                if (typeof decimals === "number") {
                    return value.toFixed(decimals);
                }
                return Math.abs(value - Math.round(value)) < 0.01
                    ? String(Math.round(value))
                    : value.toFixed(1);
            }

            function clamp(value, min, max) {
                return Math.min(Math.max(value, min), max);
            }

            function scaleValue(value, inMin, inMax, outMin, outMax) {
                if (inMax === inMin) return outMin;
                const t = clamp((value - inMin) / (inMax - inMin), 0, 1);
                return outMin + t * (outMax - outMin);
            }

            function updateVisualHeights(primaryThickness, secondaryThickness, foundation, bedFrame, personHeight) {
                const mattressLeftPx = scaleValue(primaryThickness, 8, 20, 36, 70);
                const mattressRightPx = secondaryThickness
                    ? scaleValue(secondaryThickness, 8, 20, 36, 70)
                    : mattressLeftPx;
                const foundationPx = scaleValue(foundation, 0, 24, 22, 46);
                const bedFramePx = scaleValue(bedFrame, 0, 24, 26, 58);

                if (stackMattressLeft)
                    stackMattressLeft.style.height = mattressLeftPx.toFixed(1) + "px";
                if (stackFoundationLeft)
                    stackFoundationLeft.style.height = foundationPx.toFixed(1) + "px";
                if (stackFrameLeft)
                    stackFrameLeft.style.height = bedFramePx.toFixed(1) + "px";

                if (stackMattressRight)
                    stackMattressRight.style.height = mattressRightPx.toFixed(1) + "px";
                if (stackFoundationRight)
                    stackFoundationRight.style.height = foundationPx.toFixed(1) + "px";
                if (stackFrameRight)
                    stackFrameRight.style.height = bedFramePx.toFixed(1) + "px";

                const figure = guide.querySelector("[data-thickness-guide-figure]");
                if (figure) {
                    const isMobile = window.matchMedia("(max-width: 768px)").matches;
                    const baseHeight = scaleValue(personHeight, 48, 96, 260, 420);
                    const figureHeight = isMobile ? Math.max(260, baseHeight * 0.8) : baseHeight;
                    const figureHeightPx = figureHeight.toFixed(1) + "px";
                    figure.style.height = figureHeightPx;

                    var figureImgsContainer = figure.querySelector(".thickness-guide-figure-imgs");
                    if (figureImgsContainer) {
                        figureImgsContainer.style.height = figureHeightPx;
                        figure.querySelectorAll(".thickness-guide-figure-img-wrap").forEach(function (wrap) {
                            wrap.style.height = figureHeightPx;
                        });
                    }

                    figure.querySelectorAll(".thickness-guide-figure-img").forEach(function (figureImg) {
                        figureImg.style.height = "100%";
                        figureImg.style.width = "auto";
                    });

                    const silhouette = figure.querySelector(".thickness-guide-silhouette");
                    if (silhouette) {
                        silhouette.style.height = "100%";
                    }
                }

                const dash = guide.querySelector(".thickness-guide-dash");
                const calculator = guide.querySelector(".thickness-guide-calculator");
                if (dash && calculator && stackMattressLeft) {
                    const calcRect = calculator.getBoundingClientRect();
                    const mattressRect = stackMattressLeft.getBoundingClientRect();
                    const top = mattressRect.top - calcRect.top + 6;
                    dash.style.top = top.toFixed(1) + "px";
                    dash.style.bottom = "auto";
                }
            }

            function updateRangeFill(slider) {
                if (!slider) return;
                const min = parseNum(slider.min || 0);
                const max = parseNum(slider.max || 100);
                const value = parseNum(slider.value || min);
                const percent = max === min ? 0 : ((value - min) / (max - min)) * 100;
                slider.style.setProperty("--track-fill", percent.toFixed(1) + "%");
            }

            function setStackLabels(stackMattress, stackFoundation, stackFrame, thickness, foundation, bedFrame) {
                function setText(el, text) {
                    if (!el) return;
                    // First try to find text element inside the element
                    let textEl = el.querySelector(".thickness-guide-stack-text");
                    // If not found, check if element has a parent container with a sibling text element
                    if (!textEl && el.parentElement && el.parentElement.classList.contains("bed-frame-container")) {
                        textEl = el.parentElement.querySelector(".thickness-guide-stack-text");
                    }
                    if (textEl) {
                        textEl.textContent = text;
                    }
                    else {
                        el.textContent = text;
                    }
                }

                setText(stackMattress, "Thickness mattress " + formatInches(thickness) + '"');
                setText(stackFoundation, foundation > 0
                    ? "Foundation (box spring) " + formatInches(foundation, 2) + '"'
                    : "Foundation (box spring) —");
                setText(stackFrame, bedFrame > 0
                    ? "Bed frame (base) " + formatInches(bedFrame, 2) + '"'
                    : "Bed frame (base) —");
            }

            function updateTotal() {
                const thicknesses = getSelectedThicknesses();
                const primaryThickness = thicknesses[0] || 12;
                const secondaryThickness = thicknesses[1] || null;
                const foundation = foundationInput ? parseNum(foundationInput.value) : 0;
                const bedFrame = bedFrameInput ? parseNum(bedFrameInput.value) : 0;
                const personHeight = heightTotalSlider ? parseNum(heightTotalSlider.value) : 48;

                const totalPrimary = primaryThickness + foundation + bedFrame;
                if (totalValueLeft)
                    totalValueLeft.textContent = formatInches(totalPrimary, 1) + '"';
                setStackLabels(stackMattressLeft, stackFoundationLeft, stackFrameLeft, primaryThickness, foundation, bedFrame);
                updateVisualHeights(primaryThickness, secondaryThickness, foundation, bedFrame, personHeight);

                if (secondaryThickness !== null && !isNaN(secondaryThickness)) {
                    const totalSecondary = secondaryThickness + foundation + bedFrame;
                    if (totalValueRight)
                        totalValueRight.textContent = formatInches(totalSecondary, 1) + '"';
                    setStackLabels(stackMattressRight, stackFoundationRight, stackFrameRight, secondaryThickness, foundation, bedFrame);
                    if (resultsRight)
                        resultsRight.hidden = false;
                }
                else if (resultsRight) {
                    resultsRight.hidden = true;
                }
            }

            if (optionsEl) {
                optionsEl.querySelectorAll(".thickness-guide-option-input").forEach(function (input) {
                    input.addEventListener("change", function () {
                        const val = parseNum(input.getAttribute("data-thickness-inches") || input.value);
                        const currentIndex = selectedThicknesses.indexOf(val);
                        const maxCount = singleSelect ? 1 : 2;

                        if (input.checked) {
                            if (singleSelect) {
                                selectedThicknesses = [val];
                            } else {
                                selectedThicknesses = [val].concat(selectedThicknesses.filter(function (v) { return v !== val; }));
                                if (selectedThicknesses.length > maxCount) {
                                    selectedThicknesses = selectedThicknesses.slice(0, maxCount);
                                }
                            }
                        }
                        else {
                            if (currentIndex > -1) {
                                selectedThicknesses.splice(currentIndex, 1);
                            }
                            if (!selectedThicknesses.length) {
                                selectedThicknesses = [val];
                            }
                        }

                        syncThicknessOptionStates();
                        updateTotal();
                    });
                });

                var activeInputs = optionsEl.querySelectorAll(".thickness-guide-option-input:checked");
                var maxCount = singleSelect ? 1 : 2;
                if (activeInputs.length) {
                    selectedThicknesses = Array.from(activeInputs)
                        .map(function (input) { return parseNum(input.getAttribute("data-thickness-inches") || input.value); })
                        .slice(0, maxCount);
                }
                else {
                    var firstInput = optionsEl.querySelector(".thickness-guide-option-input");
                    if (firstInput) {
                        selectedThicknesses = [parseNum(firstInput.getAttribute("data-thickness-inches") || firstInput.value)];
                    }
                }

                syncThicknessOptionStates();
            }

            // Illustration image toggle (Men / Women) – in left/right form columns, same style as thickness-guide-field
            const figureToggles = guide.querySelectorAll("[data-thickness-guide-figure-toggle]");
            const figureContainer = guide.querySelector("[data-thickness-guide-figure]");
            const wraps = figureContainer ? figureContainer.querySelectorAll(".thickness-guide-figure-img-wrap") : [];
            if (figureToggles.length && wraps.length && !guide.dataset.thicknessFigureToggleBound) {
                figureToggles.forEach(function (toggleEl) {
                    toggleEl.querySelectorAll("[data-thickness-guide-figure-btn]").forEach(function (btn) {
                        btn.addEventListener("click", function () {
                            const id = btn.getAttribute("data-thickness-guide-figure-btn");
                            guide.querySelectorAll("[data-thickness-guide-figure-btn]").forEach(function (b) {
                                var active = b.getAttribute("data-thickness-guide-figure-btn") === id;
                                b.classList.toggle("is-checked", active);
                                b.setAttribute("aria-pressed", active ? "true" : "false");
                            });
                            wraps.forEach(function (wrap) {
                                wrap.classList.toggle("thickness-guide-figure-img-wrap--active", wrap.getAttribute("data-thickness-guide-img") === id);
                            });
                        });
                    });
                });
                guide.dataset.thicknessFigureToggleBound = "true";
            }

            function syncFoundation() {
                if (foundationSlider && foundationInput) {
                    foundationInput.value = foundationSlider.value;
                    updateRangeFill(foundationSlider);
                    updateTotal();
                }
            }
            function syncFoundationInput() {
                if (foundationSlider && foundationInput) {
                    foundationSlider.value = foundationInput.value;
                    updateRangeFill(foundationSlider);
                    updateTotal();
                }
            }
            if (foundationSlider) {
                foundationSlider.addEventListener("input", syncFoundation);
                foundationSlider.addEventListener("change", syncFoundation);
            }
            if (foundationInput) {
                foundationInput.addEventListener("input", syncFoundationInput);
                foundationInput.addEventListener("change", syncFoundationInput);
            }

            function syncBedFrame() {
                if (bedFrameSlider && bedFrameInput) {
                    bedFrameInput.value = bedFrameSlider.value;
                    updateRangeFill(bedFrameSlider);
                    updateTotal();
                }
            }
            function syncBedFrameInput() {
                if (bedFrameSlider && bedFrameInput) {
                    bedFrameSlider.value = bedFrameInput.value;
                    updateRangeFill(bedFrameSlider);
                    updateTotal();
                }
            }
            if (bedFrameSlider) {
                bedFrameSlider.addEventListener("input", syncBedFrame);
                bedFrameSlider.addEventListener("change", syncBedFrame);
            }
            if (bedFrameInput) {
                bedFrameInput.addEventListener("input", syncBedFrameInput);
                bedFrameInput.addEventListener("change", syncBedFrameInput);
            }

            function heightTotalToFtIn(totalIn) {
                const ft = Math.floor(totalIn / 12);
                const inVal = Math.round(totalIn % 12);
                return { ft: ft, in: inVal };
            }
            function ftInToTotal(ft, inVal) {
                return parseNum(ft) * 12 + parseNum(inVal);
            }
            function syncHeightFromSlider() {
                if (heightTotalSlider && heightFtInput && heightInInput) {
                    const t = parseNum(heightTotalSlider.value);
                    const o = heightTotalToFtIn(t);
                    heightFtInput.value = o.ft;
                    heightInInput.value = o.in;
                    updateRangeFill(heightTotalSlider);
                    updateTotal();
                }
            }
            function syncHeightFromInputs() {
                if (heightTotalSlider && heightFtInput && heightInInput) {
                    const total = ftInToTotal(heightFtInput.value, heightInInput.value);
                    heightTotalSlider.value = Math.min(96, Math.max(48, total));
                    updateRangeFill(heightTotalSlider);
                    updateTotal();
                }
            }
            if (heightTotalSlider) {
                heightTotalSlider.addEventListener("input", syncHeightFromSlider);
                heightTotalSlider.addEventListener("change", syncHeightFromSlider);
            }
            if (heightFtInput) {
                heightFtInput.addEventListener("input", syncHeightFromInputs);
                heightFtInput.addEventListener("change", syncHeightFromInputs);
            }
            if (heightInInput) {
                heightInInput.addEventListener("input", syncHeightFromInputs);
                heightInInput.addEventListener("change", syncHeightFromInputs);
            }

            updateTotal();
            updateRangeFill(foundationSlider);
            updateRangeFill(bedFrameSlider);
            updateRangeFill(heightTotalSlider);
            guide._thicknessUpdateTotal = updateTotal;
        }
        if (document.readyState === "loading") {
            document.addEventListener("DOMContentLoaded", initThicknessGuideCalculator);
        } else {
            initThicknessGuideCalculator();
        }
        document.addEventListener("shopify:section:load", function () {
            setTimeout(initThicknessGuideCalculator, 100);
        });

        // Use event delegation on document level to catch dynamically added links
        if ($) {
            // Use jQuery event delegation (only attach once)
            if (!$(document).data("thicknessGuideHandlerAttached")) {
                $(document).on(
                    "click",
                    'a[data-thickness-guide="true"], a.thickness-guide-link',
                    function (e) {
                        if (isThicknessGuideButton(this)) {
                            e.preventDefault();
                            e.stopPropagation();
                            openThicknessGuideDrawer();
                            return false;
                        }
                    }
                );
                $(document).data("thicknessGuideHandlerAttached", true);
            }
        } else {
            // Use vanilla JS event delegation (only attach once)
            if (!document.thicknessGuideHandlerAttached) {
                document.addEventListener("click", function (e) {
                    const target = e.target.closest(
                        'a[data-thickness-guide="true"], a.thickness-guide-link'
                    );
                    if (target && isThicknessGuideButton(target)) {
                        e.preventDefault();
                        e.stopPropagation();
                        openThicknessGuideDrawer();
                        return false;
                    }
                });
                document.thicknessGuideHandlerAttached = true;
            }
        }
    }

    // Size guide links are handled by the global .kik_guide listener above.
    function attachSizeGuideClickHandlers() {
        return;
    }

    function addSizeOptionClass() {
        // Use jQuery if available, otherwise use vanilla JS
        const $ = window.jQuery || window.$;

        if ($) {
            // Check both in swatches container and product info
            $(
                ".tt-swatches-container-js .tt-title-options, .tt-product-single-info .tt-title-options"
            ).each(function () {
                const $titleElement = $(this);
                const textContent = $titleElement.text() || "";
                const titleText = textContent.toLowerCase();

                // Check if title contains "size" word
                if (titleText.indexOf("size") > -1) {
                    const $parentWrapper = $titleElement.closest(".tt-wrapper");
                    if ($parentWrapper.length) {
                        $parentWrapper.addClass("size-option");

                        // Add Size Guide link if it doesn't exist
                        if (
                            $titleElement.find('a[data-target="#modalProductInfo"]')
                                .length === 0
                        ) {
                            const $sizeGuideLink = $(
                                '<a data-toggle="modal" data-target="#modalProductInfo" href="#">Size Guide</a>'
                            );
                            $titleElement.append($sizeGuideLink);
                        }
                    }
                }
            });
        } else {
            // Fallback to vanilla JS
            const titleOptions = document.querySelectorAll(
                ".tt-swatches-container-js .tt-title-options, .tt-product-single-info .tt-title-options"
            );

            titleOptions.forEach(function (titleElement) {
                const textContent = titleElement.textContent || "";
                const titleText = textContent.toLowerCase();

                // Check if title contains "size" word
                if (titleText.indexOf("size") > -1) {
                    const parentWrapper = titleElement.closest(".tt-wrapper");
                    if (parentWrapper) {
                        parentWrapper.classList.add("size-option");

                        // Add Size Guide link if it doesn't exist
                        const existingLink = titleElement.querySelector(
                            'a[data-target="#modalProductInfo"]'
                        );
                        if (!existingLink) {
                            const sizeGuideLink = document.createElement("a");
                            sizeGuideLink.setAttribute("data-toggle", "modal");
                            sizeGuideLink.setAttribute("data-target", "#modalProductInfo");
                            sizeGuideLink.setAttribute("href", "#");
                            sizeGuideLink.textContent = "Size Guide";
                            titleElement.appendChild(sizeGuideLink);
                        }
                    }
                }
            });
        }

        // Attach click handlers after adding links
        attachSizeGuideClickHandlers();
    }

    function addThicknessOptionClass() {
        // Use jQuery if available, otherwise use vanilla JS
        const $ = window.jQuery || window.$;

        if ($) {
            // Check both in swatches container and product info
            $(
                ".tt-swatches-container-js .tt-title-options, .tt-product-single-info .tt-title-options"
            ).each(function () {
                const $titleElement = $(this);
                const textContent = $titleElement.text() || "";
                const titleText = textContent.toLowerCase();

                // Check if title contains "thickness" word
                if (titleText.indexOf("thickness") > -1) {
                    const $parentWrapper = $titleElement.closest(".tt-wrapper");
                    if ($parentWrapper.length) {
                        $parentWrapper.addClass("thickness-option");

                        // Remove the original text content but keep child elements
                        const $titleClone = $titleElement.clone();
                        $titleClone.children().remove();
                        const originalText = $titleClone.text().trim();
                        // Clear direct text content
                        $titleElement
                            .contents()
                            .filter(function () {
                                return this.nodeType === 3; // Text node
                            })
                            .remove();

                        // Function to update the selected value display
                        function updateThicknessSelectedValue() {
                            // Try multiple selectors to find the selected option
                            let $selectedOption = $parentWrapper.find(
                                ".tt-options-swatch li.active a"
                            );

                            // If not found, try other common selectors
                            if (!$selectedOption.length) {
                                $selectedOption = $parentWrapper.find(
                                    ".tt-options-swatch li.selected a"
                                );
                            }
                            if (!$selectedOption.length) {
                                $selectedOption = $parentWrapper.find(
                                    ".tt-options-swatch li[data-availability='true']:first a"
                                );
                            }
                            if (!$selectedOption.length) {
                                // Try to find first non-disabled option
                                $selectedOption = $parentWrapper.find(
                                    ".tt-options-swatch li:not([data-availability='false']) a:first"
                                );
                            }
                            if (!$selectedOption.length) {
                                $selectedOption = $parentWrapper.find(
                                    ".tt-options-swatch li:first a"
                                );
                            }

                            if ($selectedOption.length) {
                                const selectedText = $selectedOption.text().trim();
                                if (selectedText) {
                                    // Remove existing current_option_name if any
                                    $titleElement.find(".current_option_name").remove();
                                    // Add selected value in green
                                    const $currentOptionName = $(
                                        '<span class="current_option_name">' +
                                        selectedText +
                                        "</span>"
                                    );
                                    $titleElement.prepend($currentOptionName);
                                }
                            }
                        }

                        // Update the selected value display (with multiple attempts)
                        updateThicknessSelectedValue();
                        setTimeout(updateThicknessSelectedValue, 100);
                        setTimeout(updateThicknessSelectedValue, 300);
                        setTimeout(updateThicknessSelectedValue, 500);

                        // Add Thickness Guide link if it doesn't exist
                        if (
                            $titleElement.find('a[data-thickness-guide="true"]').length === 0
                        ) {
                            const $thicknessGuideLink = $(
                                '<a class="thickness-guide-link" data-thickness-guide="true" href="#">THICKNESS GUIDE</a>'
                            );
                            $titleElement.append($thicknessGuideLink);
                        }

                        // Update selected value when option changes
                        $parentWrapper
                            .find(".tt-options-swatch li a")
                            .off("click.thickness")
                            .on("click.thickness", function () {
                                setTimeout(function () {
                                    updateThicknessSelectedValue();
                                }, 100);
                            });

                        // Use MutationObserver to watch for class changes
                        if (typeof MutationObserver !== "undefined") {
                            const optionObserver = new MutationObserver(function (mutations) {
                                mutations.forEach(function (mutation) {
                                    if (
                                        mutation.type === "attributes" &&
                                        mutation.attributeName === "class"
                                    ) {
                                        setTimeout(function () {
                                            updateThicknessSelectedValue();
                                        }, 50);
                                    }
                                });
                            });

                            const optionElements = $parentWrapper.find(
                                ".tt-options-swatch li"
                            );
                            optionElements.each(function () {
                                optionObserver.observe(this, {
                                    attributes: true,
                                    attributeFilter: ["class"],
                                });
                            });
                        }
                    }
                }
            });
        } else {
            // Fallback to vanilla JS
            const titleOptions = document.querySelectorAll(
                ".tt-swatches-container-js .tt-title-options, .tt-product-single-info .tt-title-options"
            );

            titleOptions.forEach(function (titleElement) {
                const textContent = titleElement.textContent || "";
                const titleText = textContent.toLowerCase();

                // Check if title contains "thickness" word
                if (titleText.indexOf("thickness") > -1) {
                    const parentWrapper = titleElement.closest(".tt-wrapper");
                    if (parentWrapper) {
                        parentWrapper.classList.add("thickness-option");

                        // Remove the original text content but keep child elements
                        const textNodes = [];
                        for (let i = 0; i < titleElement.childNodes.length; i++) {
                            const node = titleElement.childNodes[i];
                            if (node.nodeType === 3) {
                                // Text node
                                textNodes.push(node);
                            }
                        }
                        textNodes.forEach(function (node) {
                            node.remove();
                        });

                        // Function to update the selected value display
                        function updateThicknessSelectedValue() {
                            // Try multiple selectors to find the selected option
                            let selectedOption = parentWrapper.querySelector(
                                ".tt-options-swatch li.active a"
                            );

                            // If not found, try other common selectors
                            if (!selectedOption) {
                                selectedOption = parentWrapper.querySelector(
                                    ".tt-options-swatch li.selected a"
                                );
                            }
                            if (!selectedOption) {
                                selectedOption = parentWrapper.querySelector(
                                    ".tt-options-swatch li[data-availability='true'] a"
                                );
                            }
                            if (!selectedOption) {
                                // Try to find first non-disabled option
                                selectedOption = parentWrapper.querySelector(
                                    ".tt-options-swatch li:not([data-availability='false']) a"
                                );
                            }
                            if (!selectedOption) {
                                selectedOption = parentWrapper.querySelector(
                                    ".tt-options-swatch li:first-child a"
                                );
                            }

                            if (selectedOption) {
                                const selectedText = selectedOption.textContent.trim();
                                if (selectedText) {
                                    // Remove existing current_option_name if any
                                    const existingCurrentOption = titleElement.querySelector(
                                        ".current_option_name"
                                    );
                                    if (existingCurrentOption) {
                                        existingCurrentOption.remove();
                                    }
                                    // Add selected value in green
                                    const currentOptionName = document.createElement("span");
                                    currentOptionName.classList.add("current_option_name");
                                    currentOptionName.textContent = selectedText;
                                    titleElement.insertBefore(
                                        currentOptionName,
                                        titleElement.firstChild
                                    );
                                }
                            }
                        }

                        // Update the selected value display (with multiple attempts)
                        updateThicknessSelectedValue();
                        setTimeout(updateThicknessSelectedValue, 100);
                        setTimeout(updateThicknessSelectedValue, 300);
                        setTimeout(updateThicknessSelectedValue, 500);

                        // Add Thickness Guide link if it doesn't exist
                        const existingLink = titleElement.querySelector(
                            'a[data-thickness-guide="true"]'
                        );
                        if (!existingLink) {
                            const thicknessGuideLink = document.createElement("a");
                            thicknessGuideLink.classList.add("thickness-guide-link");
                            thicknessGuideLink.setAttribute("data-thickness-guide", "true");
                            thicknessGuideLink.setAttribute("href", "#");
                            thicknessGuideLink.textContent = "THICKNESS GUIDE";
                            titleElement.appendChild(thicknessGuideLink);
                        }

                        // Update selected value when option changes
                        const optionLinks = parentWrapper.querySelectorAll(
                            ".tt-options-swatch li a"
                        );
                        optionLinks.forEach(function (link) {
                            link.addEventListener("click", function () {
                                setTimeout(function () {
                                    updateThicknessSelectedValue();
                                }, 100);
                            });
                        });

                        // Use MutationObserver to watch for class changes
                        if (typeof MutationObserver !== "undefined") {
                            const optionObserver = new MutationObserver(function (mutations) {
                                mutations.forEach(function (mutation) {
                                    if (
                                        mutation.type === "attributes" &&
                                        mutation.attributeName === "class"
                                    ) {
                                        setTimeout(function () {
                                            updateThicknessSelectedValue();
                                        }, 50);
                                    }
                                });
                            });

                            const optionElements = parentWrapper.querySelectorAll(
                                ".tt-options-swatch li"
                            );
                            optionElements.forEach(function (element) {
                                optionObserver.observe(element, {
                                    attributes: true,
                                    attributeFilter: ["class"],
                                });
                            });
                        }
                    }
                }
            });
        }

        // Attach click handlers after adding links
        attachThicknessGuideClickHandlers();
    }

    // Function to initialize with multiple attempts
    function initSizeOptionDetection() {
        addSizeOptionClass();

        // Try multiple times to catch dynamically loaded content
        setTimeout(addSizeOptionClass, 100);
        setTimeout(addSizeOptionClass, 300);
        setTimeout(addSizeOptionClass, 500);
        setTimeout(addSizeOptionClass, 1000);
        setTimeout(addSizeOptionClass, 2000);
    }

    // Function to initialize thickness option detection with multiple attempts
    function initThicknessOptionDetection() {
        addThicknessOptionClass();

        // Try multiple times to catch dynamically loaded content
        setTimeout(addThicknessOptionClass, 100);
        setTimeout(addThicknessOptionClass, 300);
        setTimeout(addThicknessOptionClass, 500);
        setTimeout(addThicknessOptionClass, 1000);
        setTimeout(addThicknessOptionClass, 2000);
    }

    // Initialize when jQuery is ready (if available)
    const $ = window.jQuery || window.$;
    if ($) {
        $(document).ready(function () {
            initSizeOptionDetection();
        });

        // Also check when swatches container content changes
        let checkInterval = null;
        const checkSwatchesContainer = function () {
            const hasTitleOptions =
                $(".tt-swatches-container-js .tt-title-options").length > 0;
            if (hasTitleOptions) {
                addSizeOptionClass();
                // Stop checking after we find elements (they should be stable)
                if (checkInterval) {
                    clearInterval(checkInterval);
                    checkInterval = null;
                }
            }
        };

        // Check periodically for swatches container (stop after 10 seconds)
        let checkCount = 0;
        checkInterval = setInterval(function () {
            checkSwatchesContainer();
            checkCount++;
            if (checkCount >= 20) {
                // Stop after 10 seconds (20 * 500ms)
                clearInterval(checkInterval);
                checkInterval = null;
            }
        }, 500);
    } else {
        // Fallback to vanilla JS
        if (document.readyState === "loading") {
            document.addEventListener("DOMContentLoaded", function () {
                initSizeOptionDetection();
            });
        } else {
            initSizeOptionDetection();
        }
    }

    // Re-run after swatches are created/updated (using MutationObserver)
    if (typeof MutationObserver !== "undefined") {
        const observer = new MutationObserver(function (mutations) {
            let shouldCheck = false;
            mutations.forEach(function (mutation) {
                if (
                    mutation.addedNodes.length > 0 ||
                    mutation.type === "characterData"
                ) {
                    shouldCheck = true;
                }
            });
            if (shouldCheck) {
                setTimeout(addSizeOptionClass, 50);
                setTimeout(addThicknessOptionClass, 50);
            }
        });

        // Observe changes in the product info section (which contains swatches container)
        const productInfo = document.querySelector(".tt-product-single-info");

        if (productInfo) {
            observer.observe(productInfo, {
                childList: true,
                subtree: true,
                characterData: true,
            });
        }

        // Also try to observe swatches container if it exists or gets created
        function observeSwatchesContainer() {
            const swatchesContainer = document.querySelector(
                ".tt-swatches-container-js"
            );
            if (swatchesContainer) {
                observer.observe(swatchesContainer, {
                    childList: true,
                    subtree: true,
                    characterData: true,
                });
            }
        }

        // Try to observe swatches container immediately and after delays
        observeSwatchesContainer();
        setTimeout(observeSwatchesContainer, 100);
        setTimeout(observeSwatchesContainer, 500);
        setTimeout(observeSwatchesContainer, 1000);
    }

    // Initialize thickness option detection when jQuery is ready (if available)
    if ($) {
        $(document).ready(function () {
            initThicknessOptionDetection();
        });

        // Also check when swatches container content changes
        let checkThicknessInterval = null;
        const checkThicknessSwatchesContainer = function () {
            const hasTitleOptions =
                $(".tt-swatches-container-js .tt-title-options").length > 0;
            if (hasTitleOptions) {
                addThicknessOptionClass();
                // Stop checking after we find elements (they should be stable)
                if (checkThicknessInterval) {
                    clearInterval(checkThicknessInterval);
                    checkThicknessInterval = null;
                }
            }
        };

        // Check periodically for swatches container (stop after 10 seconds)
        let checkThicknessCount = 0;
        checkThicknessInterval = setInterval(function () {
            checkThicknessSwatchesContainer();
            checkThicknessCount++;
            if (checkThicknessCount >= 20) {
                // Stop after 10 seconds (20 * 500ms)
                clearInterval(checkThicknessInterval);
                checkThicknessInterval = null;
            }
        }, 500);
    } else {
        // Fallback to vanilla JS
        if (document.readyState === "loading") {
            document.addEventListener("DOMContentLoaded", function () {
                initThicknessOptionDetection();
            });
        } else {
            initThicknessOptionDetection();
        }
    }

    if (!window.__kiksThicknessVariantChangeListenerBound) {
        document.addEventListener("variant:change", function () {
            setTimeout(addThicknessOptionClass, 100);
        });
        window.__kiksThicknessVariantChangeListenerBound = true;
    }

    // Re-initialize on section load (for Shopify theme editor)
    if (typeof document.addEventListener !== "undefined") {
        document.addEventListener("shopify:section:load", function (event) {
            setTimeout(initSizeOptionDetection, 100);
            setTimeout(initThicknessOptionDetection, 100);
            setTimeout(initKikVideoModal, 100);
        });
    }

    // ============================================================================
    // Kik: Watch Video Modal
    // ============================================================================
    function initKikVideoModal() {
        const triggers = document.querySelectorAll('[data-kik-video-trigger]');
        const modals = document.querySelectorAll('[data-kik-video-modal]');

        if (!triggers.length || !modals.length) return;

        function openModal(modal) {
            modal.setAttribute('aria-hidden', 'false');
            document.body.style.overflow = 'hidden';

            // Start playing video
            const iframe = modal.querySelector('[data-kik-video-iframe]');
            const video = modal.querySelector('[data-kik-video-element]');

            if (iframe) {
                const src = iframe.getAttribute('data-src');
                if (src && !iframe.getAttribute('src')) {
                    iframe.setAttribute('src', src);
                }
            }
            if (video) {
                video.play().catch(function () { /* autoplay may be blocked */ });
            }
        }

        function closeModal(modal) {
            modal.setAttribute('aria-hidden', 'true');
            document.body.style.overflow = '';

            // Stop playing
            const iframe = modal.querySelector('[data-kik-video-iframe]');
            const video = modal.querySelector('[data-kik-video-element]');

            if (iframe) {
                iframe.removeAttribute('src');
            }
            if (video) {
                video.pause();
                video.currentTime = 0;
            }
        }

        triggers.forEach(function (trigger) {
            trigger.addEventListener('click', function (e) {
                e.preventDefault();
                e.stopPropagation();

                // Find the nearest modal (same section)
                const closestSection = trigger.closest('[data-product-media-container]') ||
                                       trigger.closest('product-media') ||
                                       document;
                let modal = closestSection.querySelector('[data-kik-video-modal]');

                // Fallback: find any modal on the page
                if (!modal) {
                    modal = document.querySelector('[data-kik-video-modal]');
                }

                if (modal) {
                    openModal(modal);
                }
            });
        });

        modals.forEach(function (modal) {
            // Close button and overlay
            const closeElements = modal.querySelectorAll('[data-kik-video-modal-close]');
            closeElements.forEach(function (el) {
                el.addEventListener('click', function (e) {
                    e.preventDefault();
                    closeModal(modal);
                });
            });

            // Close on Escape key
            document.addEventListener('keydown', function (e) {
                if (e.key === 'Escape' && modal.getAttribute('aria-hidden') === 'false') {
                    closeModal(modal);
                }
            });
        });
    }

    // Initialize on DOM ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initKikVideoModal);
    } else {
        initKikVideoModal();
    }
})();

// Kik: Product Inventory & Pre-Order Update on Variant Change
(function() {
    'use strict';

    function toNumber(value) {
        if (typeof value === 'number' && !Number.isNaN(value)) return value;
        if (typeof value === 'string' && value.trim() !== '') {
            const parsed = Number(value);
            if (!Number.isNaN(parsed)) return parsed;
        }
        return null;
    }

    function parseStockBlockId(id) {
        if (!id) return null;
        const match = id.match(/^product-stock-(\d+)-(.+)$/);
        if (!match) return null;
        return {
            productId: match[1],
            sectionId: match[2]
        };
    }

    function getStockBlock(productId, sectionId) {
        if (!productId) return null;

        if (sectionId) {
            const exact = document.getElementById(`product-stock-${productId}-${sectionId}`);
            if (exact) return exact;
        }

        return document.querySelector(`[id^="product-stock-${productId}-"]`);
    }

    function getStockContainer(stockBlock) {
        if (!stockBlock) return null;
        return stockBlock.querySelector('.product-stock');
    }

    function isVariantPreorder(variant) {
        if (!variant || variant.inventory_policy !== 'continue') return false;
        const quantity = toNumber(variant.inventory_quantity);
        return quantity !== null && quantity <= 0;
    }

    function getVariantInventoryLevel(variant, inventoryThreshold) {
        const quantity = toNumber(variant.inventory_quantity);
        const variantStockSize = quantity === null ? 0 : quantity;

        if (variant.inventory_policy === 'continue' && variantStockSize <= 0) {
            return { level: 'preorder', variantStockSize };
        }

        if (variant.available === false) {
            return { level: 'zero', variantStockSize };
        }

        if (variantStockSize < inventoryThreshold) {
            return { level: 'low', variantStockSize };
        }

        return { level: 'high', variantStockSize };
    }

    function getVariantPreorderMessage(stockBlock, variantId, fallbackMessage) {
        if (!stockBlock || !variantId) return fallbackMessage;

        const scriptEl = stockBlock.querySelector('script[data-variant-preorder-messages]');
        if (!scriptEl || !scriptEl.textContent) return fallbackMessage;

        try {
            const variantPreorderMessages = JSON.parse(scriptEl.textContent);
            const variantMessage = variantPreorderMessages[variantId];
            if (variantMessage && String(variantMessage).trim() !== '') {
                return String(variantMessage).trim();
            }
        } catch (e) {
            // keep fallback message
        }

        return fallbackMessage;
    }

    function setInventoryLabel(labelElement, text, isSoldOut) {
        if (!labelElement) return;

        labelElement.textContent = '';

        if (isSoldOut) {
            const soldOut = document.createElement('span');
            soldOut.className = 'no_stock';
            soldOut.textContent = text;
            labelElement.appendChild(soldOut);
            return;
        }

        labelElement.textContent = text;
    }

    function updateInventoryStatus(product, variant, sectionId) {
        if (!product || !variant) return;

        const stockBlock = getStockBlock(product.id, sectionId);
        if (!stockBlock) return;

        const stockInner = stockBlock.querySelector(`[id^="product-stock-inner-${product.id}-"]`) || stockBlock.querySelector('[id^="product-stock-inner-"]');
        if (!stockInner) return;

        const blockId = stockBlock.getAttribute('data-product-block');
        if (!blockId) return;

        const blockSettings = window.productInventorySettings?.[blockId] || {};
        const inventoryThreshold = parseInt(blockSettings.inventory_threshhold, 10) || 11;
        const noInventoryText = blockSettings.no_inventory_text || 'Out of stock';

        const { level, variantStockSize } = getVariantInventoryLevel(variant, inventoryThreshold);

        const stockContainer = stockInner.querySelector('.product-stock');
        if (stockContainer) {
            stockContainer.classList.remove('product-stock--high', 'product-stock--low', 'product-stock--zero', 'product-stock--preorder');
            stockContainer.classList.add(`product-stock--${level}`);

            if (blockSettings.show_progress_bar) {
                const maxValue = parseFloat(blockSettings.progress_bar_max_value) || 50;
                let differencePercent = (variantStockSize / maxValue) * 100;
                if (differencePercent > 100) differencePercent = 100;
                if (differencePercent < 0) differencePercent = 0;
                stockContainer.style.setProperty('--gsc-freeshipping-bar-percent', `${differencePercent}%`);
            }

            if (blockSettings.show_inventory_status === 'only_when_inventroy_low' && level === 'high' && variant.available) {
                stockContainer.classList.add('hidden');
            } else {
                stockContainer.classList.remove('hidden');
            }
        }

        const labelElement = stockInner.querySelector('.product-stock__label');
        if (labelElement) {
            if (level === 'preorder') {
                const fallbackText = blockSettings.preorder_text || 'Pre-Order Available';
                const preorderMessage = getVariantPreorderMessage(stockBlock, variant.id, fallbackText);
                setInventoryLabel(labelElement, preorderMessage, false);
            } else if (variant.available) {
                if (blockSettings.inventory_notice_type === 'counter') {
                    setInventoryLabel(labelElement, `${variantStockSize} in stock`, false);
                } else if (variantStockSize < inventoryThreshold) {
                    setInventoryLabel(labelElement, blockSettings.low_inventory_text || 'Act now, few in stock!', false);
                } else {
                    setInventoryLabel(labelElement, blockSettings.high_inventory_text || 'Many in stock', false);
                }
            } else {
                setInventoryLabel(labelElement, noInventoryText, true);
            }
        }

        const progressBar = stockInner.querySelector('.free-shipping-bar__progress-bar');
        if (progressBar) {
            progressBar.classList.remove('free-shipping-bar__progress-bar--low', 'free-shipping-bar__progress-bar--preorder');
            if (level === 'low') {
                progressBar.classList.add('free-shipping-bar__progress-bar--low');
            } else if (level === 'preorder') {
                progressBar.classList.add('free-shipping-bar__progress-bar--preorder');
            }
        }
    }

    function cacheButtonOriginalText(buttonText) {
        if (!buttonText || buttonText.dataset.preorderOriginalCached === 'true') return;

        if (!buttonText.dataset.originalText) {
            buttonText.dataset.originalText = buttonText.textContent.trim();
        }

        const longLabel = buttonText.querySelector('.product-buy-btn__text-label');
        const shortLabel = buttonText.querySelector('.product-buy-btn__text-label-short');

        if (longLabel && !buttonText.dataset.originalLongText) {
            buttonText.dataset.originalLongText = longLabel.textContent.trim();
        }

        if (shortLabel && !buttonText.dataset.originalShortText) {
            buttonText.dataset.originalShortText = shortLabel.textContent.trim();
        }

        buttonText.dataset.preorderOriginalCached = 'true';
    }

    function formatVariantPrice(variant) {
        const rawPrice = variant ? toNumber(variant.price) : null;
        if (rawPrice === null) return null;

        return '$' + (rawPrice / 100).toFixed(2);
    }

    function getActiveBundlePrice() {
        var block = document.querySelector('.bedding-offer-block.is-selected[data-bundle-price]');
        if (!block) return 0;
        var p = parseInt(block.dataset.bundlePrice, 10);
        return isNaN(p) ? 0 : p;
    }

    function buildAddToCartText(buttonText, variant) {
        const fallbackBase = 'Add To Cart';
        const mode = (buttonText?.dataset?.kiksMode || '').toLowerCase();
        if (mode === 'simple') {
            return fallbackBase;
        }
        const rawPrice = variant ? toNumber(variant.price) : null;

        if (rawPrice === null) {
            return buttonText.dataset.originalText || fallbackBase;
        }

        const totalPrice = rawPrice + getActiveBundlePrice();
        const priceText = '$' + (totalPrice / 100).toFixed(2);

        return fallbackBase + ' - ' + priceText;
    }

    function applyButtonText(button, state, variant) {
        const buttonText = button.querySelector('span[data-button-text]');
        if (!buttonText) return;

        cacheButtonOriginalText(buttonText);

        const longLabel = buttonText.querySelector('.product-buy-btn__text-label');
        const shortLabel = buttonText.querySelector('.product-buy-btn__text-label-short');

        if (state === 'preorder') {
            button.disabled = false;
            button.classList.add('product-form__btn--preorder');

            if (longLabel || shortLabel) {
                if (longLabel) longLabel.textContent = 'Pre-Order';
                if (shortLabel) shortLabel.textContent = 'Pre-Order';
            } else {
                buttonText.textContent = 'Pre-Order';
            }
            return;
        }

        if (state === 'soldout') {
            button.disabled = true;
            button.classList.remove('product-form__btn--preorder');

            if (longLabel || shortLabel) {
                if (longLabel) longLabel.textContent = 'Sold out';
                if (shortLabel) shortLabel.textContent = 'Sold out';
            } else {
                buttonText.textContent = 'Sold out';
            }
            return;
        }

        button.classList.remove('product-form__btn--preorder');

        if (state === 'default') {
            button.disabled = false;
            const addToCartText = buildAddToCartText(buttonText, variant);

            if (longLabel || shortLabel) {
                if (longLabel) longLabel.textContent = addToCartText;
                if (shortLabel) shortLabel.textContent = addToCartText;
            } else {
                buttonText.textContent = addToCartText;
            }
            return;
        }

        // unknown: keep disabled state untouched, only restore non-disabled text
        if (!button.disabled) {
            const addToCartText = buildAddToCartText(buttonText, variant);

            if (longLabel || shortLabel) {
                if (longLabel) longLabel.textContent = addToCartText;
                if (shortLabel) shortLabel.textContent = addToCartText;
            } else {
                buttonText.textContent = addToCartText;
            }
        }
    }

    function getInventoryStateFromDom(productId, sectionId) {
        const stockBlock = getStockBlock(productId, sectionId);
        const stockContainer = getStockContainer(stockBlock);

        if (!stockContainer) {
            return { isAvailable: null, isPreorder: false };
        }

        if (stockContainer.classList.contains('product-stock--preorder')) {
            return { isAvailable: true, isPreorder: true };
        }

        if (stockContainer.classList.contains('product-stock--zero') || stockContainer.classList.contains('product-no-stock')) {
            return { isAvailable: false, isPreorder: false };
        }

        return { isAvailable: true, isPreorder: false };
    }

    function updateButtonText(product, variant, sectionId) {
        if (!product || !product.id) return;

        let isAvailable = null;
        let isPreorder = false;
        let hasInventoryQuantity = false;
        let hasInventoryPolicy = false;

        if (variant && typeof variant.available === 'boolean') {
            isAvailable = variant.available;
        }

        if (variant) {
            hasInventoryQuantity = toNumber(variant.inventory_quantity) !== null;
            hasInventoryPolicy = typeof variant.inventory_policy === 'string' && variant.inventory_policy !== '';
            isPreorder = isVariantPreorder(variant);
        }

        // Fallback to DOM only when variant inventory fields are incomplete.
        if (isAvailable === null || !hasInventoryQuantity || !hasInventoryPolicy) {
            const domState = getInventoryStateFromDom(product.id, sectionId);
            if (isAvailable === null) isAvailable = domState.isAvailable;
            if (!hasInventoryQuantity || !hasInventoryPolicy) isPreorder = domState.isPreorder;
        }

        let buttonState = 'unknown';
        if (isAvailable === false) {
            buttonState = 'soldout';
        } else if (isPreorder && isAvailable !== false) {
            buttonState = 'preorder';
        } else if (isAvailable === true) {
            buttonState = 'default';
        }

        const forms = document.querySelectorAll(`form[id*="product-form-${product.id}"]`);
        forms.forEach(function(form) {
            const buttons = form.querySelectorAll('button.product-form__btn');
            buttons.forEach(function(button) {
                applyButtonText(button, buttonState, variant);
            });
        });
    }

    function initInventorySettings() {
        window.productInventorySettings = window.productInventorySettings || {};

        document.querySelectorAll('[data-product-block]').forEach(function(block) {
            const blockId = block.getAttribute('data-product-block');
            const stockContainer = block.querySelector('.product-stock');

            if (!blockId || !stockContainer) return;

            window.productInventorySettings[blockId] = {
                show_progress_bar: stockContainer.classList.contains('product-stock--with-progress'),
                inventory_threshhold: parseInt(block.dataset.inventoryThreshold, 10) || 11,
                show_inventory_status: block.dataset.showInventoryStatus || 'always',
                inventory_notice_type: block.dataset.inventoryNoticeType || 'label',
                high_inventory_text: block.dataset.highInventoryText || 'Many in stock',
                low_inventory_text: block.dataset.lowInventoryText || 'Act now, few in stock!',
                no_inventory_text: block.dataset.noInventoryText || 'Out of stock',
                preorder_text: block.dataset.preorderText || 'Pre-Order Available',
                progress_bar_max_value: block.dataset.progressBarMaxValue || '50'
            };
        });
    }

    function parseJsonScriptContent(scriptEl) {
        if (!scriptEl || !scriptEl.textContent) return null;
        try {
            return JSON.parse(scriptEl.textContent);
        } catch (e) {
            return null;
        }
    }

    function getFormForPicker(picker, productId) {
        if (!picker) return null;

        const formId = picker.getAttribute('form');
        if (formId) {
            const formById = document.forms[formId] || document.getElementById(formId);
            if (formById) return formById;
        }

        const nestedForm = picker.querySelector('form');
        if (nestedForm) return nestedForm;

        if (!productId) return null;
        return document.querySelector(`form[id^="product-form-${productId}-"]`);
    }

    function getVariantIdFromUrl() {
        try {
            const currentUrl = new URL(window.location.href);
            const parsed = toNumber(currentUrl.searchParams.get('variant'));
            return parsed === null ? null : parsed;
        } catch (e) {
            return null;
        }
    }

    function getVariantIdFromForm(form) {
        if (!form) return null;
        const idInput = form.querySelector('input[name="id"]');
        if (!idInput) return null;
        const parsed = toNumber(idInput.value);
        return parsed === null ? null : parsed;
    }

    function getVariantFromPickerVariants(variants, variantId) {
        if (!Array.isArray(variants) || variants.length === 0) return null;
        const numericId = toNumber(variantId);
        if (numericId === null) return null;
        return variants.find((variant) => toNumber(variant?.id) === numericId) || null;
    }

    function hasCompleteVariantInventory(variant) {
        if (!variant) return false;
        const hasQuantity = toNumber(variant.inventory_quantity) !== null;
        const hasPolicy = typeof variant.inventory_policy === 'string' && variant.inventory_policy !== '';
        return hasQuantity && hasPolicy;
    }

    function getVariantFromPicker(productId) {
        const picker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
        if (!picker) return null;

        const variants = parseJsonScriptContent(
            picker.querySelector('script[data-variant-picker-variants]')
        );
        const selectedVariant = parseJsonScriptContent(
            picker.querySelector('script[data-selected-variant-json]')
        );
        const form = getFormForPicker(picker, productId);

        const candidateIds = [];
        const urlVariantId = getVariantIdFromUrl();
        if (urlVariantId !== null) candidateIds.push(urlVariantId);

        const formVariantId = getVariantIdFromForm(form);
        if (formVariantId !== null) candidateIds.push(formVariantId);

        const pickerSelectedId = toNumber(picker.dataset.kiksSelectedVariantId || picker.dataset.selectedVariant);
        if (pickerSelectedId !== null) candidateIds.push(pickerSelectedId);

        const selectedVariantId = toNumber(selectedVariant?.id);
        if (selectedVariantId !== null) candidateIds.push(selectedVariantId);

        let resolvedVariant = null;
        if (Array.isArray(variants) && variants.length > 0) {
            for (let i = 0; i < candidateIds.length; i += 1) {
                resolvedVariant = getVariantFromPickerVariants(variants, candidateIds[i]);
                if (resolvedVariant) break;
            }
            if (!resolvedVariant) {
                resolvedVariant = variants.find((variant) => variant?.available) || variants[0] || null;
            }
        }

        if (!resolvedVariant && selectedVariant) {
            resolvedVariant = selectedVariant;
        }

        if (resolvedVariant && form) {
            const idInput = form.querySelector('input[name="id"]');
            if (idInput && resolvedVariant.id && idInput.value !== String(resolvedVariant.id)) {
                idInput.value = String(resolvedVariant.id);
            }
        }

        if (resolvedVariant) {
            return resolvedVariant;
        }

        const selectedVariantScript = picker.querySelector('script[data-selected-variant-json]');
        if (!selectedVariantScript || !selectedVariantScript.textContent) return null;

        try {
            return JSON.parse(selectedVariantScript.textContent);
        } catch (e) {
            return null;
        }
    }

    function initializeProductStockAndButtons() {
        const productStocks = document.querySelectorAll('[id^="product-stock-"]');
        const initializedProductIds = new Set();

        productStocks.forEach(function(stockBlock) {
            const parsed = parseStockBlockId(stockBlock.id);
            if (!parsed) return;

            const productId = parsed.productId;
            const sectionId = parsed.sectionId;
            const variant = getVariantFromPicker(productId);
            const product = { id: productId };

            if (hasCompleteVariantInventory(variant)) {
                updateInventoryStatus(product, variant, sectionId);
            }

            updateButtonText(product, variant, sectionId);
            requestAnimationFrame(function() {
                updateButtonText(product, variant, sectionId);
            });
            setTimeout(function() {
                updateButtonText(product, variant, sectionId);
            }, 150);
            initializedProductIds.add(productId);
        });

        // Fallback for products that have form but no inventory block
        document.querySelectorAll('form[id*="product-form-"]').forEach(function(form) {
            const formId = form.getAttribute('id') || '';
            const match = formId.match(/product-form-(\d+)/);
            if (!match) return;

            const productId = match[1];
            if (initializedProductIds.has(productId)) return;

            const variant = getVariantFromPicker(productId);
            updateButtonText({ id: productId }, variant, null);
            requestAnimationFrame(function() {
                updateButtonText({ id: productId }, variant, null);
            });
            setTimeout(function() {
                updateButtonText({ id: productId }, variant, null);
            }, 150);
        });
    }

    document.addEventListener('variant:change', function(event) {
        const product = event.detail?.product;
        const variant = event.detail?.variant;

        if (!product || !variant) return;

        const productStocks = document.querySelectorAll(`[id^="product-stock-${product.id}-"]`);

        if (productStocks.length === 0) {
            updateButtonText(product, variant, null);
            requestAnimationFrame(function() {
                updateButtonText(product, variant, null);
            });
            return;
        }

        productStocks.forEach(function(stockBlock) {
            const parsed = parseStockBlockId(stockBlock.id);
            if (!parsed) return;

            updateInventoryStatus(product, variant, parsed.sectionId);
            updateButtonText(product, variant, parsed.sectionId);

            requestAnimationFrame(function() {
                updateButtonText(product, variant, parsed.sectionId);
            });
        });
    });

    function initializeAll() {
        initInventorySettings();
        initializeProductStockAndButtons();
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initializeAll);
    } else {
        initializeAll();
    }

    document.addEventListener('shopify:section:load', initializeAll);
})();

(function () {
    'use strict';

    const PRODUCT_CAROUSEL_SELECTOR = 'product-media-carousel';
    const VIEWPORT_SYNC_DELAY_MS = 120;

    function getSelectedThumb(carouselRoot) {
        if (!carouselRoot) return null;
        return carouselRoot.querySelector(
            '.product-thumbnail-carousel__thumb.is-primary, .product-thumbnail-carousel-mobile__thumb.is-primary'
        );
    }

    function getMainCarouselComponent(carouselRoot) {
        if (!carouselRoot) return null;
        return carouselRoot.querySelector('carousel-component[data-product-media-carousel-component]');
    }

    function isMobileCarousel(carouselRoot) {
        return !!(carouselRoot && carouselRoot.classList.contains('product-media-carousel-mobile'));
    }

    function getMobileViewport(carouselRoot) {
        if (!isMobileCarousel(carouselRoot)) return null;
        return carouselRoot.querySelector('.product-media-carousel-mobile__viewport');
    }

    function getSelectedSlide(carouselRoot) {
        if (!carouselRoot) return null;
        const mainCarousel = getMainCarouselComponent(carouselRoot);
        const embla = mainCarousel && mainCarousel.embla;

        if (embla && typeof embla.selectedScrollSnap === 'function' && typeof embla.slideNodes === 'function') {
            const selectedIndex = embla.selectedScrollSnap();
            return embla.slideNodes()[selectedIndex] || null;
        }

        const selectedSlideByClass = carouselRoot.querySelector(
            '.product-media-carousel__slide.is-selected, .product-media-carousel-mobile__slide.is-selected'
        );
        if (selectedSlideByClass) return selectedSlideByClass;

        const selectedThumb = getSelectedThumb(carouselRoot);
        if (!selectedThumb) return null;
        const dotIndex = Number(selectedThumb.getAttribute('data-dot-index'));
        if (!Number.isFinite(dotIndex)) return null;

        const slides = carouselRoot.querySelectorAll('.product-media-carousel__slide, .product-media-carousel-mobile__slide');
        return slides[dotIndex] || null;
    }

    function getRatioPaddingByShape(shapeElement) {
        if (!shapeElement || !shapeElement.classList) return null;
        const classes = shapeElement.classList;

        if (classes.contains('shape--fit-square') || classes.contains('shape--square')) {
            return 'var(--gsc-square-image-ratio)';
        }
        if (classes.contains('shape--fit-portrait-xl') || classes.contains('shape--portrait-xl')) {
            return 'var(--gsc-large-portrait-image-ratio)';
        }
        if (classes.contains('shape--fit-portrait') || classes.contains('shape--portrait')) {
            return 'var(--gsc-portrait-image-ratio)';
        }
        if (classes.contains('shape--fit-landscape') || classes.contains('shape--landscape')) {
            return 'var(--gsc-landscape-image-ratio)';
        }
        if (classes.contains('shape--fit-default')) {
            return 'var(--gsc-default-card-image-ratio)';
        }
        if (classes.contains('shape--default')) {
            return 'var(--gsc-default-card-image-ratio)';
        }

        return null;
    }

    function updateDesktopViewportRatio(carouselRoot) {
        if (!carouselRoot || isMobileCarousel(carouselRoot)) return;

        const viewport = carouselRoot.querySelector('.product-media-carousel__viewport');
        if (!viewport) return;

        const selectedSlide = getSelectedSlide(carouselRoot);
        if (!selectedSlide) {
            viewport.style.removeProperty('padding-bottom');
            return;
        }

        const selectedShape = selectedSlide.querySelector('.shape');
        const ratioPadding = getRatioPaddingByShape(selectedShape);

        if (ratioPadding) {
            viewport.style.paddingBottom = ratioPadding;
        } else {
            viewport.style.removeProperty('padding-bottom');
        }
    }

    function updateMobileViewportHeight(carouselRoot) {
        const viewport = getMobileViewport(carouselRoot);
        if (!viewport) return;

        const selectedSlide = getSelectedSlide(carouselRoot);
        if (!selectedSlide) {
            viewport.style.removeProperty('height');
            return;
        }

        const selectedShape = selectedSlide.querySelector('.shape');
        if (!selectedShape) {
            viewport.style.removeProperty('height');
            return;
        }

        const height = Math.round(selectedShape.getBoundingClientRect().height);
        if (height > 0) {
            viewport.style.height = `${height}px`;
        } else {
            viewport.style.removeProperty('height');
        }
    }

    function syncViewportState(carouselRoot) {
        updateDesktopViewportRatio(carouselRoot);
        updateMobileViewportHeight(carouselRoot);
    }

    function scheduleViewportState(carouselRoot, delay = 0) {
        if (!carouselRoot) return;
        if (carouselRoot.__kikViewportSyncTimer) {
            clearTimeout(carouselRoot.__kikViewportSyncTimer);
        }
        carouselRoot.__kikViewportSyncTimer = setTimeout(function () {
            syncViewportState(carouselRoot);
        }, delay);
    }

    function bindEmblaEvents(carouselRoot) {
        if (!carouselRoot || carouselRoot.__kikViewportEmblaBound) return;
        let attempts = 0;

        function tryBind() {
            attempts += 1;
            const mainCarousel = getMainCarouselComponent(carouselRoot);
            const embla = mainCarousel && mainCarousel.embla;

            if (embla && typeof embla.on === 'function') {
                const refreshViewport = function () {
                    scheduleViewportState(carouselRoot, 0);
                };

                embla.on('select', refreshViewport);
                embla.on('settle', refreshViewport);
                embla.on('reInit', refreshViewport);

                carouselRoot.__kikViewportEmblaBound = true;
                scheduleViewportState(carouselRoot, 0);
                return;
            }

            if (attempts < 120) {
                requestAnimationFrame(tryBind);
            }
        }

        tryBind();
    }

    function bindViewportSync(carouselRoot) {
        if (!carouselRoot || carouselRoot.__kikViewportSyncBound) return;
        carouselRoot.__kikViewportSyncBound = true;

        const thumbs = carouselRoot.querySelectorAll(
            '.product-thumbnail-carousel__thumb, .product-thumbnail-carousel-mobile__thumb'
        );
        if (thumbs.length) {
            const observer = new MutationObserver(function (mutations) {
                for (const mutation of mutations) {
                    if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                        scheduleViewportState(carouselRoot, VIEWPORT_SYNC_DELAY_MS);
                        break;
                    }
                }
            });

            thumbs.forEach(function (thumb) {
                observer.observe(thumb, { attributes: true, attributeFilter: ['class'] });
            });
        }

        if (!carouselRoot.__kikViewportLoadBound) {
            carouselRoot.__kikViewportLoadBound = true;
            carouselRoot.addEventListener(
                'load',
                function (event) {
                    const target = event.target;
                    if (target && target.tagName === 'IMG') {
                        scheduleViewportState(carouselRoot, 0);
                    }
                },
                true
            );
        }

        if (!carouselRoot.__kikViewportResizeBound) {
            carouselRoot.__kikViewportResizeBound = true;
            window.addEventListener('resize', function () {
                scheduleViewportState(carouselRoot, 60);
            });
        }

        bindEmblaEvents(carouselRoot);
        scheduleViewportState(carouselRoot, 0);
    }

    function initKikProductCarouselRatioSwitch() {
        const carousels = document.querySelectorAll(PRODUCT_CAROUSEL_SELECTOR);
        if (!carousels.length) return;
        carousels.forEach(bindViewportSync);
    }

    function syncAllProductMediaCarouselsAfterVariantChange() {
        document.querySelectorAll(PRODUCT_CAROUSEL_SELECTOR).forEach(function (root) {
            bindViewportSync(root);
            scheduleViewportState(root, VIEWPORT_SYNC_DELAY_MS);
        });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initKikProductCarouselRatioSwitch);
    } else {
        initKikProductCarouselRatioSwitch();
    }

    if (!window.__kikProductCarouselVariantViewportSync) {
        window.__kikProductCarouselVariantViewportSync = true;
        document.addEventListener('variant:change', syncAllProductMediaCarouselsAfterVariantChange);
    }

document.addEventListener('shopify:section:load', initKikProductCarouselRatioSwitch);
})();

// ============================================================================
// Kik: PDP Affirm monthly text + prequalify integration
// ============================================================================
(function () {
    'use strict';

    if (window.__kikPdpAffirmSyncInitialized) return;
    window.__kikPdpAffirmSyncInitialized = true;

    const CONTAINER_SELECTOR = '.affirm-custom-container';
    const PREQUALIFY_SELECTOR = '.kik-affirm-prequalify';
    const HIDDEN_AFFIRM_SELECTOR = '.affirm-as-low-as';
    const MONTHLY_VALUE_SELECTOR = '.kik-affirm-monthly-message b';
    const MONEY_FALLBACK_FORMAT = '${{amount}}';
    const PRICE_TEXT_SELECTOR = '[data-kiks-current-price]';
    let latestVariantPriceCents = null;

    function toNumber(value) {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }

    function formatMoney(cents) {
        const amount = toNumber(cents);
        if (amount === null) return '$0.00';

        if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
            const format = (window.Shopify.money_format || MONEY_FALLBACK_FORMAT);
            return window.Shopify.formatMoney(amount, format);
        }

        return `$${(amount / 100).toFixed(2)}`;
    }

    function parsePriceTextToCents(priceText) {
        if (!priceText) return null;

        let normalized = String(priceText).replace(/[^0-9.,-]/g, '').trim();
        if (!normalized) return null;

        const lastComma = normalized.lastIndexOf(',');
        const lastDot = normalized.lastIndexOf('.');

        if (lastComma !== -1 && lastDot !== -1) {
            if (lastComma > lastDot) {
                normalized = normalized.replace(/\./g, '').replace(',', '.');
            } else {
                normalized = normalized.replace(/,/g, '');
            }
        } else if (lastComma !== -1) {
            if (/,\d{1,2}$/.test(normalized)) {
                normalized = normalized.replace(',', '.');
            } else {
                normalized = normalized.replace(/,/g, '');
            }
        }

        const amount = Number(normalized);
        if (!Number.isFinite(amount)) return null;
        return Math.round(amount * 100);
    }

    function getCurrentPriceCentsFromDom() {
        const priceNode = document.querySelector(PRICE_TEXT_SELECTOR);
        if (priceNode) {
            const parsed = parsePriceTextToCents(priceNode.textContent || '');
            if (parsed !== null && parsed > 0) return parsed;
        }

        const hidden = document.querySelector(`${CONTAINER_SELECTOR} ${HIDDEN_AFFIRM_SELECTOR}`);
        const hiddenAmount = hidden ? toNumber(hidden.getAttribute('data-amount')) : null;
        if (hiddenAmount !== null && hiddenAmount > 0) return hiddenAmount;

        return null;
    }

    function updateAffirmUi(priceCents) {
        const amount = toNumber(priceCents);
        if (amount === null || amount <= 0) return;

        const monthlyCents = Math.floor(amount / 12);
        const monthlyText = `${formatMoney(monthlyCents)} /month`;

        document.querySelectorAll(CONTAINER_SELECTOR).forEach(function (container) {
            const monthlyValue = container.querySelector(MONTHLY_VALUE_SELECTOR);
            if (monthlyValue) {
                monthlyValue.textContent = monthlyText;
            }

            const hiddenAffirm = container.querySelector(HIDDEN_AFFIRM_SELECTOR);
            if (hiddenAffirm) {
                hiddenAffirm.setAttribute('data-amount', String(amount));
            }
        });

        if (typeof affirm !== 'undefined' && affirm.ui && typeof affirm.ui.refresh === 'function') {
            affirm.ui.refresh();
        }
    }

    function syncAffirm(variantPriceMaybe) {
        const variantPrice = toNumber(variantPriceMaybe);
        if (variantPrice !== null && variantPrice > 0) {
            latestVariantPriceCents = variantPrice;
            updateAffirmUi(variantPrice);
            return;
        }

        const domPrice = getCurrentPriceCentsFromDom();
        if (domPrice !== null && domPrice > 0) {
            updateAffirmUi(domPrice);
            return;
        }

        if (latestVariantPriceCents !== null && latestVariantPriceCents > 0) {
            updateAffirmUi(latestVariantPriceCents);
        }
    }

    function bindPrequalifyClick() {
        document.addEventListener('click', function (event) {
            const link = event.target.closest(PREQUALIFY_SELECTOR);
            if (!link) return;

            event.preventDefault();
            const container = link.closest(CONTAINER_SELECTOR);
            const localTrigger = container ? container.querySelector('.affirm-modal-trigger') : null;
            const trigger = localTrigger || document.querySelector(`${CONTAINER_SELECTOR} .affirm-modal-trigger`);
            if (trigger) {
                trigger.click();
            }
        });
    }

    function bindVariantAndOptionEvents() {
        document.addEventListener('variant:change', function (event) {
            const variant = event && event.detail ? event.detail.variant : null;
            syncAffirm(variant ? variant.price : null);

            setTimeout(function () {
                syncAffirm(null);
            }, 80);
        });

        document.addEventListener('change', function (event) {
            const target = event.target;
            if (!(target instanceof Element)) return;

            if (
                target.name === 'id' ||
                target.matches(
                    'input[name^="option"], select[name^="option"], [data-variant-picker-option-inputs] input, [data-variant-picker-option-inputs] select'
                )
            ) {
                setTimeout(function () {
                    syncAffirm(null);
                }, 80);
            }
        });
    }

    function bindPriceObserver() {
        const body = document.body || document.documentElement;
        if (!body) return;

        let observedPriceNode = null;

        function observePriceNode() {
            const priceNode = document.querySelector(PRICE_TEXT_SELECTOR);
            if (!priceNode || priceNode === observedPriceNode) return;

            observedPriceNode = priceNode;
            if (priceNode.__kikAffirmPriceObserved) return;
            priceNode.__kikAffirmPriceObserved = true;

            const nodeObserver = new MutationObserver(function () {
                syncAffirm(null);
            });
            nodeObserver.observe(priceNode, { childList: true, subtree: true, characterData: true });
        }

        observePriceNode();

        const rootObserver = new MutationObserver(function () {
            observePriceNode();
        });
        rootObserver.observe(body, { childList: true, subtree: true });
    }

    function initKikPdpAffirmSync() {
        if (!document.querySelector(CONTAINER_SELECTOR)) return;

        bindPrequalifyClick();
        bindVariantAndOptionEvents();
        bindPriceObserver();

        syncAffirm(null);
        requestAnimationFrame(function () {
            syncAffirm(null);
        });
        setTimeout(function () {
            syncAffirm(null);
        }, 120);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initKikPdpAffirmSync);
    } else {
        initKikPdpAffirmSync();
    }

    // ============================================================================
    // Kik: Sticky Add to Cart — move bar into footer when scrolled to footer, restore when scroll up
    // ============================================================================
    function initKikStickyAddToCartFooter() {
        var stickyBar = document.querySelector('.kik_sticky_add_to_cart');
        var footer = document.querySelector('.shopify-section-group-main-footer-group');
        if (!stickyBar || !footer) return;

        var originalParent = stickyBar.parentNode;
        var originalNextSibling = stickyBar.nextSibling;

        var footerObserver = new IntersectionObserver(
            function (entries) {
                entries.forEach(function (entry) {
                    if (entry.isIntersecting) {
                        // Scrolled to footer: append sticky bar into footer (only when it's the active/visible one)
                        if (stickyBar.parentNode !== footer) {
                            footer.appendChild(stickyBar);
                        }
                    } else {
                        // Scrolled up: restore sticky bar to original position
                        if (stickyBar.parentNode === footer) {
                            if (originalNextSibling) {
                                originalParent.insertBefore(stickyBar, originalNextSibling);
                            } else {
                                originalParent.appendChild(stickyBar);
                            }
                        }
                    }
                });
            },
            { root: null, threshold: 0 }
        );

        footerObserver.observe(footer);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initKikStickyAddToCartFooter);
    } else {
        initKikStickyAddToCartFooter();
    }

})();
